import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { getFunctions } from "firebase/functions";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  Activity,
  ArrowLeft,
  Bell,
  BriefcaseBusiness,
  Building2,
  CalendarCheck,
  Camera,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileCheck2,
  FileText,
  Lock,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Upload,
  UserCheck,
  UserPlus,
  Wallet,
  XCircle,
} from "lucide-react";
import { auth, db, storage } from "../firebaseConfig";
import VideoInterviewRoom from "../components/interview/VideoInterviewRoom";

const interviewFunctions = getFunctions(undefined, "asia-south1");

const DEPARTMENTS = [
  { id: "admin-hr", name: "Administration & Human Resources", icon: "HR" },
  { id: "finance-accounts", name: "Finance & Accounts", icon: "FA" },
  { id: "procurement-ops", name: "Procurement & Operations", icon: "PO" },
  { id: "member-services", name: "Member Services & Cooperative Affairs", icon: "MS" },
  { id: "projects-planning", name: "Projects & Planning", icon: "PP" },
  { id: "it-digital", name: "IT & Digital Systems", icon: "IT" },
  { id: "legal-compliance", name: "Legal & Compliance", icon: "LC" },
  { id: "support-services", name: "Support Services", icon: "SS" },
];

const APPLICATION_STAGES = [
  ["APPLIED", "Application Received"],
  ["DOCUMENT_VERIFIED", "Document Verification"],
  ["SHORTLISTED", "ATS Shortlisting"],
  ["INTERVIEW_SCHEDULED", "Interview Process"],
  ["PENDING_CEO_APPROVAL", "Management Approval"],
  ["OFFER_RELEASED", "Offer Released"],
  ["CANDIDATE_ACCEPTED", "Candidate Accepted"],
  ["EMPLOYEE_CREATED", "Employee Activated"],
];

const initialRegister = {
  name: "",
  email: "",
  mobile: "",
  password: "",
  confirmPassword: "",
};
const initialApplication = {
  dob: "",
  gender: "",
  qualification: "",
  experienceYears: "",
  skills: "",
  state: "",
  city: "",
  address: "",
  currentCompany: "",
  currentSalary: "",
  expectedSalary: "",
  noticePeriod: "",
  resume: null,
};

const clean = (value) => String(value || "").trim();
const todayIso = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
};
const attendanceTime = () =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).format(new Date());
const getDistanceMeters = (lat1, lon1, lat2, lon2) => {
  const radius = 6371000;
  const toRadians = (value) => (value * Math.PI) / 180;
  const latitudeDelta = toRadians(lat2 - lat1);
  const longitudeDelta = toRadians(lon2 - lon1);
  const value =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(longitudeDelta / 2) ** 2;
  return radius * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
};
const createDeviceFingerprintHash = async (uid) => {
  if (!globalThis.crypto?.subtle) {
    throw new Error("Secure browser fingerprinting unavailable. Please use an HTTPS browser session.");
  }
  const screenSignature =
    typeof window !== "undefined" && window.screen
      ? `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`
      : "screen-unavailable";
  const fingerprintSource = [
    uid,
    navigator.userAgent || "",
    navigator.platform || "",
    navigator.language || "",
    String(navigator.hardwareConcurrency || ""),
    String(navigator.deviceMemory || ""),
    Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    screenSignature,
  ].join("|");
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(fingerprintSource)
  );
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};
const leaveDays = (fromDate, toDate) => {
  if (!fromDate || !toDate) return 0;
  const start = new Date(`${fromDate}T00:00:00`);
  const end = new Date(`${toDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;
  return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
};
const formatMoney = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const safeFileName = (value) =>
  clean(value).replace(/[^a-zA-Z0-9._-]+/g, "-").slice(-120) || "document";
const formatDate = (value) => {
  if (!value) return "-";
  if (value?.toDate) return value.toDate().toLocaleString("en-IN");
  return String(value);
};
const statusClass = (status) => {
  if (["ACTIVE", "LIVE", "OFFER_RELEASED", "CANDIDATE_ACCEPTED", "EMPLOYEE_CREATED", "COMPLETED", "PRESENT", "APPROVED", "PAID"].includes(status))
    return "bg-emerald-100 text-emerald-700";
  if (["REJECTED", "BLOCKED", "CANCELLED", "NO_SHOW"].includes(status))
    return "bg-red-100 text-red-700";
  if (String(status).includes("INTERVIEW")) return "bg-violet-100 text-violet-700";
  if (status === "WAITING") return "bg-violet-100 text-violet-700";
  if (status === "EXPIRED") return "bg-slate-200 text-slate-700";
  return "bg-amber-100 text-amber-700";
};

function Badge({ value }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] ${statusClass(value)}`}>
      {String(value || "PENDING").replaceAll("_", " ")}
    </span>
  );
}

function Field({ label, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</span>
      <input
        {...props}
        className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
      />
    </label>
  );
}

function Select({ label, children, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</span>
      <select
        {...props}
        className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm font-medium text-slate-900 outline-none transition hover:border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
      >
        {children}
      </select>
    </label>
  );
}

function Button({ children, tone = "blue", className = "", ...props }) {
  const tones = {
    blue: "bg-blue-600 text-white shadow-sm hover:bg-blue-700",
    dark: "bg-[#10233f] text-white shadow-sm hover:bg-[#183354]",
    green: "bg-emerald-600 text-white hover:bg-emerald-700",
    red: "bg-red-600 text-white hover:bg-red-700",
    amber: "bg-amber-400 text-slate-950 hover:bg-amber-300",
    light: "border border-slate-200 bg-white text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50",
  };
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${tones[tone]} ${className}`}
    >
      {children}
    </button>
  );
}

async function readCollection(name, constraints = []) {
  const source = constraints.length
    ? query(collection(db, name), ...constraints)
    : collection(db, name);
  const snapshot = await getDocs(source);
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

function calculateAts(form, job) {
  let score = 15;
  const requirements = `${job?.experience || ""} ${job?.requirements || ""} ${job?.skills || ""}`.toLowerCase();
  const candidateSkills = clean(form.skills)
    .toLowerCase()
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  const matchedSkills = candidateSkills.filter((skill) => requirements.includes(skill)).length;
  score += Math.min(30, candidateSkills.length * 5 + matchedSkills * 5);
  score += clean(form.qualification) ? 20 : 0;
  score += Number(form.experienceYears || 0) >= 3 ? 20 : Number(form.experienceYears || 0) > 0 ? 12 : 5;
  score += form.resume ? 10 : 0;
  score += clean(form.city) && clean(form.state) ? 5 : 0;
  return Math.min(100, score);
}

export default function Career() {
  const [interviewInviteId, setInterviewInviteId] = useState(() =>
    new URLSearchParams(window.location.search).get("interview") || ""
  );
  const [interviewAccessToken, setInterviewAccessToken] = useState(() =>
    new URLSearchParams(window.location.search).get("invite") || ""
  );
  const [jobs, setJobs] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [account, setAccount] = useState(null);
  const [applications, setApplications] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [employeeRequests, setEmployeeRequests] = useState([]);
  const [reimbursements, setReimbursements] = useState([]);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("LOGIN");
  const [register, setRegister] = useState(initialRegister);
  const [login, setLogin] = useState({ email: "", password: "" });
  const [application, setApplication] = useState(initialApplication);
  const [applyOpen, setApplyOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authRestoring, setAuthRestoring] = useState(true);
  const [notice, setNotice] = useState("");
  const [showPortal, setShowPortal] = useState(true);

  const notify = (message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 4000);
  };

  const loadJobs = async () => {
    try {
      // Firestore rules allow candidates to read only published/active jobs.
      // The status restriction must be part of the query itself because
      // Firestore security rules are not post-query filters.
      const records = await readCollection("hr_job_postings", [
        where("status", "in", ["PUBLISHED", "ACTIVE"]),
      ]);
      setJobs(
        records.filter(
          (item) =>
            ["PUBLISHED", "ACTIVE"].includes(String(item.status).toUpperCase()) &&
            item.isDeleted !== true &&
            item.isActive !== false
        )
      );
    } catch (error) {
      console.error(error);
      notify(`Vacancy load error: ${error.message}`);
    }
  };

  useEffect(() => {
    loadJobs();
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser || firebaseUser.isAnonymous) {
        setAuthRestoring(false);
        return;
      }
      try {
        const profileSnap = await getDoc(
          doc(db, "candidate_users", firebaseUser.uid)
        );
        if (!profileSnap.exists()) {
          setAuthRestoring(false);
          return;
        }
        const restored = { id: profileSnap.id, ...profileSnap.data() };
        if (restored.status !== "ACTIVE") {
          await signOut(auth);
          setAccount(null);
          notify(
            restored.status === "BLOCKED"
              ? "Candidate account blocked hai."
              : "Candidate account active nahi hai."
          );
          return;
        }
        setAccount(restored);
        await loadPortalData(restored);
      } catch (error) {
        console.error("Candidate session restore failed", error);
      } finally {
        setAuthRestoring(false);
      }
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!interviewInviteId || account || authRestoring) return;
    setAuthMode("LOGIN");
    setAuthOpen(true);
    notify("Secure interview join karne ke liye apne Candidate Portal login se verify karein.");
  }, [interviewInviteId, account, authRestoring]);

  const departmentCards = useMemo(
    () =>
      DEPARTMENTS.map((department) => ({
        ...department,
        jobs: jobs.filter(
          (job) =>
            job.departmentId === department.id ||
            clean(job.department).toLowerCase() === department.name.toLowerCase() ||
            clean(job.deptName).toLowerCase() === department.name.toLowerCase()
        ),
      })),
    [jobs]
  );

  const visibleJobs = useMemo(
    () =>
      selectedDepartment
        ? departmentCards.find((item) => item.id === selectedDepartment)?.jobs || []
        : [],
    [departmentCards, selectedDepartment]
  );

  const loadPortalData = async (candidate) => {
    const appResult = await Promise.allSettled([
      readCollection("job_applications", [
        where("candidateUserId", "==", candidate.id),
      ]),
    ]);
    if (appResult[0].status === "fulfilled") setApplications(appResult[0].value);

    if (candidate.portalMode !== "EMPLOYEE" || !candidate.employeeId) return;

    const results = await Promise.allSettled([
      readCollection("employees", [where("candidateUserId", "==", candidate.id)]),
      readCollection("hr_onboarding", [where("candidateUserId", "==", candidate.id)]),
      readCollection("hr_documents", [where("candidateUserId", "==", candidate.id)]),
      readCollection("payroll_records", [where("candidateUserId", "==", candidate.id)]),
      readCollection("attendance_logs", [where("candidateUserId", "==", candidate.id)]),
      readCollection("hr_leave_requests", [where("candidateUserId", "==", candidate.id)]),
      readCollection("hr_employee_requests", [where("candidateUserId", "==", candidate.id)]),
      readCollection("hr_reimbursements", [where("candidateUserId", "==", candidate.id)]),
    ]);
    const value = (index) =>
      results[index].status === "fulfilled" ? results[index].value : [];
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        console.warn(`ESS collection ${index} could not load`, result.reason);
      }
    });

    const foundEmployee = value(0).find((item) => item.id === candidate.employeeId);
    setEmployee(foundEmployee || null);
    setOnboarding(value(1).find((item) => item.employeeId === candidate.employeeId) || null);
    setDocuments(value(2).filter((item) => item.employeeId === candidate.employeeId));
    setPayroll(value(3).filter((item) => item.employeeId === candidate.employeeId));
    setAttendance(value(4).filter((item) => item.employeeId === candidate.employeeId));
    setLeaves(value(5).filter((item) => item.employeeId === candidate.employeeId));
    setEmployeeRequests(value(6).filter((item) => item.employeeId === candidate.employeeId));
    setReimbursements(value(7).filter((item) => item.employeeId === candidate.employeeId));
  };

  const createAccount = async (event) => {
    event.preventDefault();
    if (!clean(register.name) || !clean(register.email) || !clean(register.mobile))
      return notify("Name, email aur mobile required hai");
    if (register.password.length < 8)
      return notify("Password minimum 8 characters ka hona chahiye");
    if (register.password !== register.confirmPassword)
      return notify("Confirm password match nahi ho raha");
    setLoading(true);
    try {
      const credential = await createUserWithEmailAndPassword(
        auth,
        clean(register.email).toLowerCase(),
        register.password
      );
      await updateProfile(credential.user, { displayName: clean(register.name) });
      await setDoc(doc(db, "candidate_users", credential.user.uid), {
        uid: credential.user.uid,
        name: clean(register.name),
        email: clean(register.email).toLowerCase(),
        mobile: clean(register.mobile),
        status: "ACTIVE",
        portalMode: "CANDIDATE",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const created = {
        id: credential.user.uid,
        uid: credential.user.uid,
        name: clean(register.name),
        email: clean(register.email).toLowerCase(),
        mobile: clean(register.mobile),
        status: "ACTIVE",
        portalMode: "CANDIDATE",
      };
      setAccount(created);
      setAuthOpen(false);
      setRegister(initialRegister);
      notify("Account created. Ab aap vacancy apply kar sakte hain.");
      if (selectedJob) {
        setShowPortal(false);
        setApplyOpen(true);
      } else {
        setShowPortal(true);
      }
    } catch (error) {
      notify(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loginAccount = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(
        auth,
        clean(login.email).toLowerCase(),
        login.password
      );
      const profileSnap = await getDoc(
        doc(db, "candidate_users", credential.user.uid)
      );
      if (!profileSnap.exists()) {
        await signOut(auth);
        return notify("Candidate profile nahi mila.");
      }
      const found = { id: profileSnap.id, ...profileSnap.data() };
      if (found.status === "BLOCKED") {
        await signOut(auth);
        return notify("Aapka candidate login application rejection ke karan blocked hai.");
      }
      if (found.status !== "ACTIVE") {
        await signOut(auth);
        return notify("Account active nahi hai.");
      }
      setAccount(found);
      setAuthOpen(false);
      await loadPortalData(found);
      if (selectedJob && found.portalMode !== "EMPLOYEE") {
        setShowPortal(false);
        setApplyOpen(true);
      } else {
        setShowPortal(true);
      }
      notify(
        found.portalMode === "EMPLOYEE"
          ? "Employee Portal login successful"
          : "Candidate Portal login successful"
      );
    } catch (error) {
      notify(error.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await signOut(auth);
    setAccount(null);
    setApplications([]);
    setEmployee(null);
    setOnboarding(null);
    setDocuments([]);
    setPayroll([]);
    setAttendance([]);
    setLeaves([]);
    setEmployeeRequests([]);
    setReimbursements([]);
    setSelectedJob(null);
    setApplyOpen(false);
    setShowPortal(true);
  };

  const startApply = (job) => {
    setSelectedJob(job);
    if (!account) {
      setAuthMode("REGISTER");
      setAuthOpen(true);
      notify("Apply karne se pehle candidate account create karein.");
      return;
    }
    if (account.portalMode === "EMPLOYEE") {
      notify("Employee account se candidate vacancy apply nahi ho sakti.");
      return;
    }
    setShowPortal(false);
    setApplyOpen(true);
  };

  const browseVacancies = () => {
    setSelectedDepartment("");
    setSelectedJob(null);
    setDetailOpen(false);
    setApplyOpen(false);
    setShowPortal(false);
  };

  const openJobDetail = (job) => {
    setSelectedJob(job);
    setDetailOpen(true);
  };

  const submitApplication = async (event) => {
    event.preventDefault();
    if (!account || !selectedJob) return;
    if (!application.qualification || !application.resume)
      return notify("Qualification aur resume mandatory hai");
    if (
      applications.some(
        (item) =>
          item.jobId === selectedJob.id &&
          !["REJECTED", "WITHDRAWN"].includes(item.status)
      )
    ) {
      return notify("Aap is vacancy ke liye already apply kar chuke hain.");
    }
    setLoading(true);
    try {
      const timestamp = Date.now();
      // Browser File objects cannot be stored inside a Firestore document.
      // Keep the selected file only for Storage upload and save its URL/name
      // in Firestore with the remaining serializable application fields.
      const { resume: resumeFile, ...applicationFields } = application;
      const filePath = `career-resumes/${account.id}/${timestamp}-${resumeFile.name}`;
      const fileRef = ref(storage, filePath);
      await uploadBytes(fileRef, resumeFile);
      const resumeUrl = await getDownloadURL(fileRef);
      const atsScore = calculateAts(application, selectedJob);
      const applicationNumber = `SAAM-${new Date().getFullYear()}-${String(
        timestamp
      ).slice(-7)}`;
      const payload = {
        candidateUserId: account.id,
        candidateName: account.name,
        name: account.name,
        email: account.email,
        mobile: account.mobile,
        jobId: selectedJob.id,
        postingId: selectedJob.id,
        jobTitle: selectedJob.title,
        department: selectedJob.department || selectedJob.deptName || "",
        deptName: selectedJob.department || selectedJob.deptName || "",
        applicationNumber,
        ...applicationFields,
        resumeUrl,
        resumeName: resumeFile.name,
        resumeStoragePath: filePath,
        atsScore,
        atsDecision: atsScore >= 75 ? "AI_SHORTLIST_RECOMMENDED" : "HR_REVIEW",
        status: "APPLIED",
        stage: "APPLICATION",
        loginStatus: "ACTIVE",
        source: "CAREER_PORTAL",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const refDoc = await addDoc(collection(db, "job_applications"), payload);
      setApplications([{ id: refDoc.id, ...payload, createdAt: new Date().toISOString() }, ...applications]);
      setApplyOpen(false);
      setApplication(initialApplication);
      setShowPortal(true);
      notify(`Application submitted: ${applicationNumber}`);
    } catch (error) {
      console.error(error);
      notify(`Application failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const acceptOffer = async (item) => {
    if (item.status !== "OFFER_RELEASED") return;
    setLoading(true);
    try {
      await updateDoc(doc(db, "job_applications", item.id), {
        status: "CANDIDATE_ACCEPTED",
        offerAcceptedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await loadPortalData(account);
      notify("Offer accepted. HR ab employee activation process start karega.");
    } catch (error) {
      notify(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (authRestoring && interviewInviteId) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#071b33] p-5 text-white">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-cyan-300/25 border-t-cyan-300" />
          <h1 className="mt-5 text-2xl font-black">Secure Interview Access</h1>
          <p className="mt-2 text-sm font-semibold text-slate-300">
            Candidate identity verify ho rahi hai...
          </p>
        </div>
      </div>
    );
  }

  if (account && interviewInviteId) {
    return (
      <VideoInterviewRoom
        firebaseFunctions={interviewFunctions}
        interviewId={interviewInviteId}
        inviteId={interviewAccessToken}
        viewerRole="CANDIDATE"
        viewerName={account.name}
        currentUser={auth.currentUser}
        candidate={account}
        onClose={() => {
          const url = new URL(window.location.href);
          url.searchParams.delete("interview");
          url.searchParams.delete("invite");
          window.history.replaceState({}, "", `${url.pathname}${url.search}`);
          setInterviewInviteId("");
          setInterviewAccessToken("");
          loadPortalData(account);
        }}
      />
    );
  }

  if (account && showPortal) {
    return (
      <Portal
        account={account}
        applications={applications}
        employee={employee}
        onboarding={onboarding}
        documents={documents}
        payroll={payroll}
        attendance={attendance}
        leaves={leaves}
        employeeRequests={employeeRequests}
        reimbursements={reimbursements}
        logout={logout}
        refresh={() => loadPortalData(account)}
        notice={notice}
        notify={notify}
        browseVacancies={browseVacancies}
        acceptOffer={acceptOffer}
        openInterview={(inviteId) => {
          const url = new URL(window.location.href);
          url.searchParams.set("interview", inviteId);
          url.searchParams.delete("invite");
          window.history.replaceState({}, "", `${url.pathname}${url.search}`);
          setInterviewInviteId(inviteId);
          setInterviewAccessToken("");
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f6fa] text-slate-900">
      {notice && (
        <div className="fixed right-5 top-5 z-[100] max-w-sm rounded-2xl bg-slate-950 px-5 py-4 text-sm font-bold text-white shadow-2xl">
          {notice}
        </div>
      )}
      <section className="relative overflow-hidden bg-[#071b33] text-white">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-blue-600/25 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-5 py-14 sm:px-8 lg:py-20">
          <div className="flex flex-wrap items-start justify-between gap-8">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-300/10 px-4 py-2 text-xs font-black tracking-widest text-cyan-100">
                <Sparkles size={15} /> DIGITAL CAREER & TALENT PORTAL
              </div>
              <h1 className="mt-6 text-4xl font-black leading-tight sm:text-6xl">
                Build your career with Shine Agri
              </h1>
              <p className="mt-5 max-w-2xl text-base font-medium leading-7 text-slate-300">
                Secure candidate account, department-wise vacancies, live
                application tracking, interview updates and seamless employee
                onboarding—all through one permanent login.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {account ? (
                <>
                  <Button tone="light" onClick={() => setShowPortal(true)}>
                    <UserCheck size={17} /> My Portal
                  </Button>
                  <Button tone="dark" onClick={logout}>
                    <LogOut size={17} /> Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button tone="light" onClick={() => { setAuthMode("LOGIN"); setAuthOpen(true); }}>
                    <LogIn size={17} /> Candidate Login
                  </Button>
                  <Button tone="green" onClick={() => { setAuthMode("REGISTER"); setAuthOpen(true); }}>
                    <UserPlus size={17} /> Create Account
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 md:grid-cols-4">
            {[
              ["Departments", DEPARTMENTS.length],
              ["Active Vacancies", jobs.length],
              ["Secure Account", "100%"],
              ["Process Tracking", "Live"],
            ].map(([label, value]) => (
              <div key={label} className="bg-[#0d2845]/95 p-5">
                <p className="text-3xl font-black">{value}</p>
                <p className="mt-1 text-[10px] font-black uppercase tracking-wider text-slate-400">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="mb-7">
          <p className="text-xs font-black uppercase tracking-[.22em] text-blue-700">Explore Careers</p>
          <h2 className="mt-2 text-3xl font-black">
            {selectedDepartment ? "Available vacancies" : "Choose a department"}
          </h2>
          <p className="mt-2 text-sm font-medium text-slate-500">
            Main page par sirf departments hain. Department open karne par HR ki live vacancies dikhengi.
          </p>
        </div>

        {!selectedDepartment ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {departmentCards.map((department) => (
              <button
                key={department.id}
                type="button"
                onClick={() => setSelectedDepartment(department.id)}
                className="group min-h-[180px] rounded-3xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-1 hover:border-blue-400 hover:shadow-xl"
              >
                <div className="flex items-start justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-950 text-sm font-black text-white group-hover:bg-blue-700">
                    {department.icon}
                  </div>
                  <ChevronRight className="text-slate-300 group-hover:text-blue-600" />
                </div>
                <h3 className="mt-5 text-lg font-black">{department.name}</h3>
                <p className="mt-2 text-xs font-bold text-slate-400">
                  {department.jobs.length
                    ? `${department.jobs.length} active vacancy`
                    : "No vacancy currently"}
                </p>
              </button>
            ))}
          </div>
        ) : (
          <div>
            <Button tone="light" className="mb-5" onClick={() => setSelectedDepartment("")}>
              <ArrowLeft size={17} /> All Departments
            </Button>
            <div className="grid gap-5 lg:grid-cols-2">
              {visibleJobs.map((job) => (
                <article key={job.id} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Badge value="ACTIVE" />
                      <h3 className="mt-3 text-2xl font-black">{job.title}</h3>
                      <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-slate-500">
                        <Building2 size={16} /> {job.department || job.deptName}
                      </p>
                    </div>
                    <div className="rounded-xl bg-blue-50 px-3 py-2 text-center">
                      <p className="text-xl font-black text-blue-700">{job.vacancies || job.posts || 1}</p>
                      <p className="text-[9px] font-black uppercase text-blue-500">Openings</p>
                    </div>
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-2 text-xs font-bold text-slate-600">
                    <div className="rounded-xl bg-slate-50 p-3">Type: {job.employmentType || "FULL TIME"}</div>
                    <div className="rounded-xl bg-slate-50 p-3">Experience: {job.experience || "As per role"}</div>
                    <div className="col-span-2 rounded-xl bg-slate-50 p-3 line-clamp-2">Location: {job.location || "As per organizational requirement"}</div>
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-3">
                    <Button tone="light" onClick={() => openJobDetail(job)}>
                      <FileText size={17} /> View Full JD
                    </Button>
                    <Button onClick={() => startApply(job)}>
                      <BriefcaseBusiness size={17} /> Login & Apply
                    </Button>
                  </div>
                </article>
              ))}
              {!visibleJobs.length && (
                <div className="col-span-2 rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center">
                  <BriefcaseBusiness className="mx-auto text-slate-300" size={42} />
                  <h3 className="mt-4 text-xl font-black">No active vacancy</h3>
                  <p className="mt-2 text-sm font-medium text-slate-500">
                    HR jab is department me vacancy publish karega, woh automatically yahan show hogi.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {authOpen && (
        <Modal title={authMode === "LOGIN" ? "Candidate / Employee Login" : "Create Candidate Account"} onClose={() => setAuthOpen(false)}>
          {authMode === "LOGIN" ? (
            <form onSubmit={loginAccount}>
              <Field label="Registered Email" type="email" value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} />
              <Field label="Password" className="mt-4" type="password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} />
              <Button type="submit" className="mt-6 w-full" disabled={loading}><LogIn size={17} /> Login to Portal</Button>
              <button type="button" onClick={() => setAuthMode("REGISTER")} className="mt-4 w-full text-sm font-black text-blue-700">New candidate? Create account</button>
            </form>
          ) : (
            <form onSubmit={createAccount}>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Full Name" value={register.name} onChange={(e) => setRegister({ ...register, name: e.target.value })} />
                <Field label="Mobile" maxLength={10} value={register.mobile} onChange={(e) => setRegister({ ...register, mobile: e.target.value.replace(/\D/g, "") })} />
                <Field label="Email" className="sm:col-span-2" type="email" value={register.email} onChange={(e) => setRegister({ ...register, email: e.target.value })} />
                <Field label="Create Password" type="password" value={register.password} onChange={(e) => setRegister({ ...register, password: e.target.value })} />
                <Field label="Confirm Password" type="password" value={register.confirmPassword} onChange={(e) => setRegister({ ...register, confirmPassword: e.target.value })} />
              </div>
              <Button type="submit" tone="green" className="mt-6 w-full" disabled={loading}><UserPlus size={17} /> Create Secure Account</Button>
              <button type="button" onClick={() => setAuthMode("LOGIN")} className="mt-4 w-full text-sm font-black text-blue-700">Already registered? Login</button>
            </form>
          )}
        </Modal>
      )}

      {detailOpen && selectedJob && (
        <JobDescriptionModal
          job={selectedJob}
          onClose={() => setDetailOpen(false)}
          onApply={() => {
            setDetailOpen(false);
            startApply(selectedJob);
          }}
        />
      )}

      {applyOpen && selectedJob && (
        <Modal title={`Apply: ${selectedJob.title}`} onClose={() => setApplyOpen(false)} wide>
          <form onSubmit={submitApplication}>
            <div className="mb-5 rounded-2xl bg-slate-950 p-4 text-white">
              <p className="text-xs font-black uppercase tracking-widest text-cyan-300">Candidate Account</p>
              <p className="mt-1 font-black">{account?.name} • {account?.email}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Date of Birth" type="date" value={application.dob} onChange={(e) => setApplication({ ...application, dob: e.target.value })} />
              <Select label="Gender" value={application.gender} onChange={(e) => setApplication({ ...application, gender: e.target.value })}><option value="">Select</option><option>Male</option><option>Female</option><option>Other</option></Select>
              <Field label="Qualification" value={application.qualification} onChange={(e) => setApplication({ ...application, qualification: e.target.value })} />
              <Field label="Experience (Years)" type="number" value={application.experienceYears} onChange={(e) => setApplication({ ...application, experienceYears: e.target.value })} />
              <Field label="Key Skills (comma separated)" className="sm:col-span-2" value={application.skills} onChange={(e) => setApplication({ ...application, skills: e.target.value })} />
              <Field label="Current Company" value={application.currentCompany} onChange={(e) => setApplication({ ...application, currentCompany: e.target.value })} />
              <Field label="Notice Period" value={application.noticePeriod} onChange={(e) => setApplication({ ...application, noticePeriod: e.target.value })} />
              <Field label="Current Salary" type="number" value={application.currentSalary} onChange={(e) => setApplication({ ...application, currentSalary: e.target.value })} />
              <Field label="Expected Salary" type="number" value={application.expectedSalary} onChange={(e) => setApplication({ ...application, expectedSalary: e.target.value })} />
              <Field label="State" value={application.state} onChange={(e) => setApplication({ ...application, state: e.target.value })} />
              <Field label="City" value={application.city} onChange={(e) => setApplication({ ...application, city: e.target.value })} />
              <label className="sm:col-span-2 block">
                <span className="mb-1.5 block text-[11px] font-black uppercase tracking-wider text-slate-500">Resume / CV *</span>
                <div className="rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50 p-5 text-center">
                  <Upload className="mx-auto text-blue-600" />
                  <input className="mt-3 text-sm" type="file" accept=".pdf,.doc,.docx" onChange={(e) => setApplication({ ...application, resume: e.target.files?.[0] || null })} />
                </div>
              </label>
            </div>
            <div className="mt-5 rounded-2xl border border-blue-200 bg-blue-50 p-4">
              <p className="text-xs font-black uppercase text-blue-500">Live ATS Match</p>
              <p className="mt-1 text-3xl font-black text-blue-800">{calculateAts(application, selectedJob)}/100</p>
            </div>
            <Button type="submit" tone="green" className="mt-6 w-full" disabled={loading}>
              <CheckCircle2 size={18} /> Submit Application
            </Button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose, wide = false }) {
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <div className={`max-h-[92vh] w-full overflow-y-auto rounded-3xl bg-white shadow-2xl ${wide ? "max-w-4xl" : "max-w-lg"}`}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-5">
          <h2 className="text-xl font-black">{title}</h2>
          <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-xl font-black">×</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function JobDescriptionModal({ job, onClose, onApply }) {
  const lines = (value) =>
    String(value || "")
      .split(/\n|•/)
      .map((item) => item.trim())
      .filter(Boolean);
  const areas = lines(job.areasCovered);
  const responsibilities = lines(job.responsibilities);
  const qualifications = lines(job.qualifications);
  const skills = lines(job.skills);
  const Section = ({ title, items, text }) => {
    if (!items?.length && !text) return null;
    return (
      <section className="border-t border-slate-200 pt-5 first:border-t-0 first:pt-0">
        <h3 className="text-sm font-black uppercase tracking-wide text-slate-950">{title}</h3>
        {text && <p className="mt-3 whitespace-pre-line text-sm font-medium leading-7 text-slate-700">{text}</p>}
        {items?.length > 0 && (
          <ul className="mt-3 space-y-2 pl-5 text-sm font-medium leading-6 text-slate-700">
            {items.map((item, index) => (
              <li key={`${item}-${index}`} className="list-disc pl-1">{item}</li>
            ))}
          </ul>
        )}
      </section>
    );
  };
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-slate-950/75 p-3 backdrop-blur-sm sm:p-6">
      <div className="max-h-[95vh] w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <header className="flex items-start justify-between gap-4 bg-[#071b33] px-6 py-6 text-white sm:px-8">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[.22em] text-cyan-300">Job Description (JD)</p>
            <h2 className="mt-2 text-2xl font-black sm:text-3xl">{job.title}</h2>
            <p className="mt-2 text-sm font-semibold text-slate-300">{job.department || job.deptName}</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-2xl">×</button>
        </header>
        <div className="max-h-[calc(95vh-180px)] overflow-y-auto">
          <div className="grid gap-px bg-slate-200 sm:grid-cols-4">
            {[
              ["Location", job.location || "-"],
              ["Experience", job.experience || "-"],
              ["Employment Type", String(job.employmentType || "FULL TIME").replaceAll("_", " ")],
              ["Compensation", job.compensation || (job.budget ? `Rs. ${Number(job.budget).toLocaleString("en-IN")} per month` : "-")],
            ].map(([label, value]) => (
              <div key={label} className="bg-slate-50 px-5 py-4">
                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{label}</p>
                <p className="mt-1 text-sm font-black text-slate-900">{value}</p>
              </div>
            ))}
          </div>
          <article className="space-y-6 px-6 py-7 sm:px-10 sm:py-9">
            <Section title="Job Overview" text={job.jobOverview || job.description || job.summary} />
            <Section title="Areas Covered" items={areas} />
            <Section title="Key Responsibilities" items={responsibilities} />
            <Section title="Required Qualifications" items={qualifications} />
            <Section title="Required Skills" items={skills} />
            <Section title="Preferred Experience" text={job.preferredExperience} />
          </article>
        </div>
        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-white px-6 py-4 sm:px-8">
          <p className="text-xs font-semibold text-slate-500">Application ke liye candidate account mandatory hai.</p>
          <div className="flex gap-3"><Button tone="light" onClick={onClose}>Close</Button><Button tone="green" onClick={onApply}><BriefcaseBusiness size={17}/> Login & Apply</Button></div>
        </footer>
      </div>
    </div>
  );
}

function Portal({
  account,
  applications,
  employee,
  onboarding,
  documents,
  payroll,
  attendance,
  leaves,
  employeeRequests,
  reimbursements,
  logout,
  refresh,
  notice,
  notify,
  browseVacancies,
  acceptOffer,
  openInterview,
}) {
  const employeeMode = account.portalMode === "EMPLOYEE";

  if (employeeMode) {
    return (
      <EmployeeEss
        account={account}
        employee={employee}
        onboarding={onboarding}
        documents={documents}
        payroll={payroll}
        attendance={attendance}
        leaves={leaves}
        employeeRequests={employeeRequests}
        reimbursements={reimbursements}
        logout={logout}
        refresh={refresh}
        notice={notice}
        notify={notify}
      />
    );
  }

  const activeInterviews = applications.filter((item) =>
    String(item.status || "").includes("INTERVIEW")
  ).length;
  const offerCount = applications.filter((item) =>
    ["OFFER_RELEASED", "CANDIDATE_ACCEPTED"].includes(item.status)
  ).length;

  return (
    <div className="min-h-screen bg-[#f6f7f4] text-slate-900">
      {notice && <PortalNotice text={notice} />}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#10233f] text-white shadow-sm"><Building2 size={19} /></div>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-[#10233f] sm:text-base">Shine Agri Talent Portal</p>
                <span className="hidden items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-emerald-700 sm:inline-flex"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500"/>Secure</span>
              </div>
              <p className="max-w-[210px] truncate text-[11px] text-slate-500 sm:max-w-none">{account.name} · {account.email}</p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button tone="light" className="hidden sm:inline-flex" onClick={refresh}><RefreshCw size={15}/> Refresh</Button>
            <Button tone="dark" onClick={logout}><LogOut size={16}/> Logout</Button>
          </div>
        </div>
        <div className="border-t border-slate-100">
          <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <ShieldCheck size={15} className="text-blue-600"/>
              <span>Candidate workspace</span>
              <ChevronRight size={13}/>
              <span className="text-slate-900">Recruitment journey</span>
            </div>
            <button type="button" onClick={browseVacancies} className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-50">
              <BriefcaseBusiness size={15}/> <span className="hidden sm:inline">Browse</span> Vacancies
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1440px] space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        <section className="grid gap-5 lg:grid-cols-[1.45fr_.55fr]">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,.04)] sm:p-8">
            <div className="flex flex-wrap items-start justify-between gap-5">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-600">Candidate command centre</p>
                <h1 className="mt-2 text-2xl font-semibold tracking-tight text-[#10233f] sm:text-3xl">Your recruitment journey</h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">Applications, interviews, documents and offer decisions—one secure, real-time workspace.</p>
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600"><ShieldCheck size={24}/></div>
            </div>
            <div className="mt-7 grid grid-cols-3 divide-x divide-slate-200 rounded-xl border border-slate-200 bg-slate-50/70">
              {[
                ["Applications", applications.length],
                ["Interviews", activeInterviews],
                ["Offers", offerCount],
              ].map(([label, value]) => (
                <div key={label} className="px-4 py-4">
                  <p className="text-xl font-semibold text-[#10233f]">{value}</p>
                  <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl bg-[#10233f] p-6 text-white shadow-[0_12px_28px_rgba(16,35,63,.12)]">
            <div className="flex items-center justify-between"><p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-cyan-300">Next best action</p><Sparkles size={18} className="text-amber-300"/></div>
            <h2 className="mt-4 text-xl font-semibold">{applications.length ? "Track your latest application" : "Find your next opportunity"}</h2>
            <p className="mt-2 text-sm leading-6 text-slate-300">{applications.length ? "Status changes and interview invitations will appear below automatically." : "Explore approved vacancies and submit your first application."}</p>
            <Button className="mt-5 w-full" onClick={browseVacancies}><BriefcaseBusiness size={16}/> Browse vacancies</Button>
          </div>
        </section>
        <div className="space-y-4">
          {applications.map((item) => (
            <ApplicationTimeline
              key={item.id}
              item={item}
              acceptOffer={acceptOffer}
              openInterview={openInterview}
            />
          ))}
          {!applications.length && (
            <Empty
              title="No application yet"
              text="Department select karke available vacancy ke liye apply karein."
              action={
                <Button onClick={browseVacancies}>
                  <BriefcaseBusiness size={17}/> Browse Vacancies & Apply
                </Button>
              }
            />
          )}
        </div>
      </main>
    </div>
  );
}

function PortalNotice({ text }) {
  return (
    <div className="fixed right-4 top-4 z-[120] max-w-sm rounded-xl border border-white/10 bg-[#10233f] px-5 py-4 text-sm font-medium text-white shadow-2xl">
      {text}
    </div>
  );
}

function EmployeeEss({
  account,
  employee,
  onboarding,
  documents,
  payroll,
  attendance,
  leaves,
  employeeRequests,
  reimbursements,
  logout,
  refresh,
  notice,
  notify,
}) {
  const [active, setActive] = useState("PORTAL");
  const [dialog, setDialog] = useState("");
  const [busy, setBusy] = useState(false);
  const [leaveForm, setLeaveForm] = useState({
    type: "CL",
    fromDate: "",
    toDate: "",
    reason: "",
  });
  const [documentForm, setDocumentForm] = useState({
    documentType: "AADHAAR",
    documentNumber: "",
    expiryDate: "",
    file: null,
  });
  const [requestForm, setRequestForm] = useState({
    requestType: "HR_SUPPORT",
    subject: "",
    description: "",
    priority: "NORMAL",
    eventDate: "",
    requestedCheckIn: "",
    requestedCheckOut: "",
    field: "",
    currentValue: "",
    requestedValue: "",
    attendanceId: "",
    attachment: null,
  });
  const [reimbursementForm, setReimbursementForm] = useState({
    category: "TRAVEL",
    amount: "",
    expenseDate: "",
    purpose: "",
    projectSite: "",
    transportMode: "",
    fromLocation: "",
    toLocation: "",
    receipt: null,
  });
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const attendanceStreamRef = useRef(null);
  const [cameraStarted, setCameraStarted] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [capturedPhotoPreview, setCapturedPhotoPreview] = useState("");
  const [locationInfo, setLocationInfo] = useState(null);
  const [attendanceAction, setAttendanceAction] = useState("");

  const employeeName = employee?.name || account.name;
  const employeeCode = employee?.employeeCode || account.employeeCode || "-";
  const identity = {
    candidateUserId: account.id,
    employeeId: employee?.id || account.employeeId,
    employeeCode,
    employeeName,
    employeeEmail: employee?.email || account.email,
    department: employee?.department || employee?.deptName || "",
    designation: employee?.designation || employee?.role || "",
  };

  const sortedAttendance = useMemo(
    () => [...attendance].sort((a, b) => String(b.date || "").localeCompare(String(a.date || ""))),
    [attendance]
  );
  const sortedPayroll = useMemo(
    () => [...payroll].sort((a, b) => String(b.month || "").localeCompare(String(a.month || ""))),
    [payroll]
  );
  const sortedReimbursements = useMemo(
    () =>
      [...reimbursements].sort((a, b) =>
        String(b.expenseDate || "").localeCompare(String(a.expenseDate || ""))
      ),
    [reimbursements]
  );
  const pendingCount =
    leaves.filter((item) => String(item.status).includes("PENDING")).length +
    documents.filter((item) => ["PENDING", "PENDING_HR_VERIFICATION"].includes(item.status)).length +
    employeeRequests.filter((item) => ["PENDING_HR_REVIEW", "IN_PROGRESS"].includes(item.status)).length +
    reimbursements.filter((item) => String(item.status || "").includes("PENDING")).length;
  const currentMonth = todayIso().slice(0, 7);
  const monthAttendance = attendance.filter((item) => String(item.date || "").startsWith(currentMonth));
  const presentDays = monthAttendance.filter((item) =>
    ["PRESENT", "LATE", "APPROVED"].includes(
      item.attendanceStatus || item.status || item.hrReviewStatus
    )
  ).length;
  const attendanceRate = monthAttendance.length
    ? Math.round((presentDays / monthAttendance.length) * 100)
    : 0;
  const todayAttendance =
    attendance.find(
      (item) => item.id === `${identity.employeeId}_${todayIso()}`
    ) || attendance.find((item) => item.date === todayIso());
  const balances = {
    CL: Number(employee?.leaveBalances?.CL ?? employee?.clBalance ?? 12),
    EL: Number(employee?.leaveBalances?.EL ?? employee?.elBalance ?? 18),
    OL: Number(employee?.leaveBalances?.OL ?? employee?.olBalance ?? 6),
    COMP_OFF: Number(employee?.leaveBalances?.COMP_OFF ?? employee?.compOffBalance ?? 0),
  };
  const openLeaveCount = leaves.filter((item) =>
    String(item.status || "").includes("PENDING")
  ).length;
  const openDocumentCount = documents.filter((item) =>
    ["PENDING", "PENDING_HR_VERIFICATION"].includes(item.status)
  ).length;
  const openRequestCount = employeeRequests.filter((item) =>
    ["PENDING_HR_REVIEW", "IN_PROGRESS"].includes(item.status)
  ).length;
  const openReimbursementCount = reimbursements.filter((item) =>
    String(item.status || "").includes("PENDING")
  ).length;
  const verifiedDocumentCount = documents.filter(
    (item) => item.status === "VERIFIED"
  ).length;
  const totalLeaveBalance = Object.values(balances).reduce(
    (total, value) => total + Number(value || 0),
    0
  );
  const officeLatitude = Number(
    employee?.attendanceLocation?.latitude ??
      employee?.officeLocation?.latitude ??
      employee?.officeLatitude
  );
  const officeLongitude = Number(
    employee?.attendanceLocation?.longitude ??
      employee?.officeLocation?.longitude ??
      employee?.officeLongitude
  );
  const officeRadiusMeters = Number(
    employee?.attendanceLocation?.radiusMeters ??
      employee?.officeLocation?.radiusMeters ??
      employee?.attendanceRadiusMeters ??
      150
  );
  const hasOfficeGeofence =
    Number.isFinite(officeLatitude) &&
    Number.isFinite(officeLongitude) &&
    officeLatitude >= -90 &&
    officeLatitude <= 90 &&
    officeLongitude >= -180 &&
    officeLongitude <= 180 &&
    officeRadiusMeters > 0;
  // Firestore and Storage both authorize attendance from the Employee Master
  // record. Keeping the UI on the same source of truth prevents a stale
  // onboarding flag from showing attendance as enabled and then failing upload.
  const attendanceEnabled = employee?.attendanceActive === true;

  const ensureEmployeeLink = () => {
    const currentUser = auth.currentUser;
    if (!currentUser || currentUser.uid !== identity.candidateUserId) {
      notify("Secure employee session verify nahi ho paayi. Please login again.");
      return false;
    }
    if (!identity.employeeId || !identity.candidateUserId) {
      notify("Employee profile link missing hai. HR se candidate UID aur Employee ID mapping check karayein.");
      return false;
    }
    return true;
  };

  const ensureAttendanceEnabled = () => {
    if (!ensureEmployeeLink()) return false;
    if (!attendanceEnabled) {
      notify("Attendance access abhi HR ne activate nahi kiya hai.");
      return false;
    }
    return true;
  };

  const stopAttendanceCamera = () => {
    const stream = attendanceStreamRef.current || videoRef.current?.srcObject;
    if (stream?.getTracks) {
      stream.getTracks().forEach((track) => track.stop());
    }
    attendanceStreamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraStarted(false);
  };

  useEffect(
    () => () => {
      const stream = attendanceStreamRef.current;
      if (stream?.getTracks) stream.getTracks().forEach((track) => track.stop());
    },
    []
  );

  useEffect(
    () => () => {
      if (capturedPhotoPreview) URL.revokeObjectURL(capturedPhotoPreview);
    },
    [capturedPhotoPreview]
  );

  const resetAttendanceCapture = () => {
    stopAttendanceCamera();
    setCapturedPhoto(null);
    setCapturedPhotoPreview("");
    setLocationInfo(null);
    setAttendanceAction("");
  };

  const openAttendanceDialog = () => {
    if (!ensureAttendanceEnabled()) return;
    resetAttendanceCapture();
    setDialog("ATTENDANCE_MARK");
  };

  const closeAttendanceDialog = () => {
    resetAttendanceCapture();
    setDialog("");
  };

  const startAttendanceCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      return notify("Is browser me camera capture supported nahi hai.");
    }
    try {
      stopAttendanceCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      attendanceStreamRef.current = stream;
      if (!videoRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        attendanceStreamRef.current = null;
        throw new Error("Camera preview initialise nahi ho paaya.");
      }
      videoRef.current.srcObject = stream;
      await videoRef.current.play().catch(() => {});
      setCameraStarted(true);
    } catch (error) {
      console.error(error);
      notify(error.message || "Camera permission allow karke dobara try karein.");
    }
  };

  const captureAttendancePhoto = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!cameraStarted || !video || !canvas || video.readyState < 2) {
      return notify("Pehle camera start karein aur preview ready hone dein.");
    }
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) return notify("Photo capture initialise nahi ho paaya.");
    context.drawImage(video, 0, 0, width, height);
    context.fillStyle = "rgba(15, 23, 42, 0.72)";
    context.fillRect(0, height - 76, width, 76);
    context.fillStyle = "#ffffff";
    context.font = `${Math.max(18, Math.round(width / 48))}px sans-serif`;
    context.fillText(`${employeeName} • ${employeeCode}`, 22, height - 43);
    context.font = `${Math.max(14, Math.round(width / 62))}px sans-serif`;
    context.fillText(`${todayIso()} ${attendanceTime()} IST`, 22, height - 16);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.86)
    );
    if (!blob) return notify("Photo capture failed. Please retry.");
    stopAttendanceCamera();
    setCapturedPhoto(blob);
    setCapturedPhotoPreview(URL.createObjectURL(blob));
  };

  const captureAttendanceLocation = async () => {
    if (!navigator.geolocation) {
      throw new Error("Is browser me GPS location supported nahi hai.");
    }
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      });
    });
    const latitude = Number(position.coords.latitude);
    const longitude = Number(position.coords.longitude);
    const accuracy = Number(position.coords.accuracy);
    const officeDistanceMeters = hasOfficeGeofence
      ? Math.round(
          getDistanceMeters(
            latitude,
            longitude,
            officeLatitude,
            officeLongitude
          )
        )
      : null;
    const insideOffice =
      hasOfficeGeofence && officeDistanceMeters <= officeRadiusMeters;
    const locationVerified = insideOffice && accuracy <= 100;
    const info = {
      latitude,
      longitude,
      accuracy,
      officeDistanceMeters,
      insideOffice,
      locationVerified,
      attendanceFlag: !hasOfficeGeofence
        ? "LOCATION_CAPTURED"
        : accuracy > 100
          ? "LOW_ACCURACY"
          : insideOffice
            ? "OFFICE"
            : "OUTSIDE_OFFICE",
      capturedAtClient: new Date().toISOString(),
    };
    setLocationInfo(info);
    return info;
  };

  const verifyAttendanceLocation = async () => {
    setAttendanceAction("LOCATION");
    try {
      await captureAttendanceLocation();
      notify("GPS location captured.");
    } catch (error) {
      console.error(error);
      notify(error.message || "Location permission allow karke dobara try karein.");
    } finally {
      setAttendanceAction("");
    }
  };

  const markAttendance = async (actionType) => {
    if (!ensureAttendanceEnabled()) return;
    if (!["CHECK_IN", "CHECK_OUT"].includes(actionType)) return;
    if (!capturedPhoto) return notify("Live camera photo capture required hai.");
    const uid = auth.currentUser.uid;
    const date = todayIso();
    const attendanceRef = doc(
      db,
      "attendance_logs",
      `${identity.employeeId}_${date}`
    );
    setBusy(true);
    setAttendanceAction(actionType);
    try {
      const existingSnapshot = await getDoc(attendanceRef);
      const existing = existingSnapshot.exists() ? existingSnapshot.data() : null;
      if (
        actionType === "CHECK_IN" &&
        (existing?.checkInAt || existing?.checkInTime)
      ) {
        throw new Error("Aaj ka check-in already recorded hai.");
      }
      if (
        actionType === "CHECK_OUT" &&
        (!existing || (!existing.checkInAt && !existing.checkInTime))
      ) {
        throw new Error("Check-out se pehle aaj ka check-in required hai.");
      }
      if (
        actionType === "CHECK_OUT" &&
        (existing?.checkOutAt || existing?.checkOutTime)
      ) {
        throw new Error("Aaj ka check-out already recorded hai.");
      }

      const location = locationInfo || (await captureAttendanceLocation());
      const deviceFingerprintHash = await createDeviceFingerprintHash(uid);
      const registeredDeviceHash =
        employee?.attendanceDeviceFingerprintHash ||
        employee?.allowedAttendanceDeviceHash ||
        existing?.checkInDeviceFingerprintHash ||
        "";
      if (
        registeredDeviceHash &&
        registeredDeviceHash !== deviceFingerprintHash
      ) {
        throw new Error(
          "This device is not registered for your attendance. HR se device reset/approval karayein."
        );
      }
      const deviceVerified = Boolean(
        registeredDeviceHash &&
          registeredDeviceHash === deviceFingerprintHash
      );
      const photoStoragePath = `hr-attendance/${uid}/${date}/${actionType}-${Date.now()}.jpg`;
      const photoRef = ref(storage, photoStoragePath);
      await uploadBytes(photoRef, capturedPhoto, {
        contentType: "image/jpeg",
        customMetadata: {
          candidateUserId: uid,
          employeeId: String(identity.employeeId),
          attendanceDate: date,
          actionType,
        },
      });

      const requiresReview =
        !location.locationVerified ||
        !deviceVerified ||
        existing?.status === "PENDING_HR_REVIEW";
      const status = requiresReview ? "PENDING_HR_REVIEW" : "PRESENT";
      const time = attendanceTime();
      const commonPayload = {
        ...identity,
        candidateUserId: uid,
        date,
        status,
        hrReviewStatus: "PENDING_HR_REVIEW",
        lastAction: actionType,
        attendanceFlag: location.attendanceFlag,
        locationVerified: location.locationVerified,
        latitude: location.latitude,
        longitude: location.longitude,
        locationAccuracy: location.accuracy,
        officeDistanceMeters: location.officeDistanceMeters,
        photoStoragePath,
        deviceFingerprintHash,
        deviceVerified,
        deviceBindingStatus: deviceVerified
          ? "REGISTERED_DEVICE"
          : "PENDING_FIRST_DEVICE_APPROVAL",
        source: "EMPLOYEE_ESS",
        updatedAt: serverTimestamp(),
      };
      const evidence = {
        latitude: location.latitude,
        longitude: location.longitude,
        accuracy: location.accuracy,
        officeDistanceMeters: location.officeDistanceMeters,
        attendanceFlag: location.attendanceFlag,
        locationVerified: location.locationVerified,
        capturedAtClient: location.capturedAtClient,
      };

      if (actionType === "CHECK_IN") {
        await setDoc(
          attendanceRef,
          {
            ...commonPayload,
            actionType: "CHECK_IN",
            checkInTime: time,
            checkInAt: serverTimestamp(),
            checkInPhotoPath: photoStoragePath,
            checkInPhotoStoragePath: photoStoragePath,
            checkInLocation: evidence,
            checkInDeviceFingerprintHash: deviceFingerprintHash,
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );
      } else {
        await updateDoc(attendanceRef, {
          checkOutTime: time,
          checkOutAt: serverTimestamp(),
          checkOutPhotoPath: photoStoragePath,
          checkOutLocation: evidence,
          checkOutDevice: { fingerprintHash: deviceFingerprintHash },
          status,
          hrReviewStatus: "PENDING_HR_REVIEW",
          updatedAt: serverTimestamp(),
        });
      }

      closeAttendanceDialog();
      await refresh();
      notify(
        status === "PRESENT"
          ? `${actionType === "CHECK_IN" ? "Check-in" : "Check-out"} recorded successfully.`
          : "Attendance captured and sent for HR review."
      );
    } catch (error) {
      console.error(error);
      notify(`Attendance failed: ${error.message}`);
    } finally {
      setBusy(false);
      setAttendanceAction("");
    }
  };

  const submitReimbursement = async (event) => {
    event.preventDefault();
    if (!ensureEmployeeLink()) return;
    const allowedCategories = [
      "TRAVEL",
      "LOCAL_CONVEYANCE",
      "LODGING",
      "MEALS",
      "OTHER",
    ];
    if (!allowedCategories.includes(reimbursementForm.category)) {
      return notify("Valid reimbursement category select karein.");
    }
    const amount = Number(reimbursementForm.amount);
    if (!Number.isFinite(amount) || amount <= 0 || amount > 10000000) {
      return notify("Valid claim amount enter karein.");
    }
    if (
      !reimbursementForm.expenseDate ||
      reimbursementForm.expenseDate > todayIso()
    ) {
      return notify("Valid expense date select karein; future date allowed nahi hai.");
    }
    if (clean(reimbursementForm.purpose).length < 8) {
      return notify("Purpose minimum 8 characters likhein.");
    }
    if (clean(reimbursementForm.projectSite).length < 2) {
      return notify("Project / site required hai.");
    }
    const isTravelClaim = ["TRAVEL", "LOCAL_CONVEYANCE"].includes(
      reimbursementForm.category
    );
    if (
      isTravelClaim &&
      (!clean(reimbursementForm.transportMode) ||
        !clean(reimbursementForm.fromLocation) ||
        !clean(reimbursementForm.toLocation))
    ) {
      return notify("Travel claim ke liye transport mode, from aur to required hain.");
    }
    const receipt = reimbursementForm.receipt;
    if (!receipt) return notify("Receipt upload required hai.");
    if (receipt.size > 10 * 1024 * 1024) {
      return notify("Receipt maximum 10 MB ho sakti hai.");
    }
    if (!["application/pdf", "image/jpeg", "image/png"].includes(receipt.type)) {
      return notify("Receipt only PDF, JPG ya PNG ho sakti hai.");
    }

    setBusy(true);
    try {
      const uid = auth.currentUser.uid;
      const claimRef = doc(collection(db, "hr_reimbursements"));
      const receiptStoragePath = `hr-reimbursements/${uid}/${claimRef.id}/${Date.now()}-${safeFileName(receipt.name)}`;
      const receiptRef = ref(storage, receiptStoragePath);
      await uploadBytes(receiptRef, receipt, {
        contentType: receipt.type,
        customMetadata: {
          candidateUserId: uid,
          employeeId: String(identity.employeeId),
          claimId: claimRef.id,
        },
      });
      const claimNumber = `REIM-${todayIso().slice(0, 4)}-${claimRef.id
        .slice(0, 8)
        .toUpperCase()}`;
      await setDoc(claimRef, {
        ...identity,
        candidateUserId: uid,
        claimId: claimRef.id,
        claimNumber,
        category: reimbursementForm.category,
        amount: Math.round(amount * 100) / 100,
        expenseDate: reimbursementForm.expenseDate,
        purpose: clean(reimbursementForm.purpose),
        projectSite: clean(reimbursementForm.projectSite),
        transportMode: isTravelClaim
          ? clean(reimbursementForm.transportMode)
          : "",
        fromLocation: isTravelClaim
          ? clean(reimbursementForm.fromLocation)
          : "",
        toLocation: isTravelClaim ? clean(reimbursementForm.toLocation) : "",
        receiptFileName: receipt.name,
        receiptStoragePath,
        receiptMimeType: receipt.type,
        receiptFileSize: receipt.size,
        status: "PENDING_HR_REVIEW",
        source: "EMPLOYEE_ESS",
        submittedByUid: uid,
        submittedByRole: "EMPLOYEE",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setReimbursementForm({
        category: "TRAVEL",
        amount: "",
        expenseDate: "",
        purpose: "",
        projectSite: "",
        transportMode: "",
        fromLocation: "",
        toLocation: "",
        receipt: null,
      });
      setDialog("");
      await refresh();
      notify(`${claimNumber} HR review ke liye submit ho gaya.`);
    } catch (error) {
      console.error(error);
      notify(`Reimbursement failed: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const submitLeave = async (event) => {
    event.preventDefault();
    if (!ensureEmployeeLink()) return;
    const days = leaveDays(leaveForm.fromDate, leaveForm.toDate);
    if (!days) return notify("From date aur To date sahi select karein.");
    if (leaveForm.fromDate < todayIso()) return notify("Past date ki leave ESS se submit nahi ho sakti.");
    if (days > 60) return notify("Ek request me maximum 60 days allowed hain.");
    if (clean(leaveForm.reason).length < 8) return notify("Leave reason minimum 8 characters likhein.");
    const overlap = leaves.some(
      (item) =>
        !["REJECTED", "CANCELLED"].includes(item.status) &&
        leaveForm.fromDate <= item.toDate &&
        leaveForm.toDate >= item.fromDate
    );
    if (overlap) return notify("Selected dates par leave request already available hai.");
    setBusy(true);
    try {
      await addDoc(collection(db, "hr_leave_requests"), {
        ...identity,
        type: leaveForm.type,
        fromDate: leaveForm.fromDate,
        toDate: leaveForm.toDate,
        days,
        reason: clean(leaveForm.reason),
        lopDays: leaveForm.type === "LOP" ? days : 0,
        requiresCEOApproval: leaveForm.type === "LOP" || days > 3,
        status: "PENDING_HR_APPROVAL",
        source: "EMPLOYEE_ESS",
        requestedByUid: account.id,
        submittedByRole: "EMPLOYEE",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setLeaveForm({ type: "CL", fromDate: "", toDate: "", reason: "" });
      setDialog("");
      await refresh();
      notify("Leave request HR ko submit ho gayi.");
    } catch (error) {
      console.error(error);
      notify(`Leave request failed: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const submitDocument = async (event) => {
    event.preventDefault();
    if (!ensureEmployeeLink()) return;
    const file = documentForm.file;
    if (!file) return notify("Document file select karein.");
    if (file.size > 5 * 1024 * 1024) return notify("Maximum file size 5 MB hai.");
    const allowed = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowed.includes(file.type)) return notify("Only PDF, JPG ya PNG upload karein.");
    setBusy(true);
    try {
      const storagePath = `hr-employee-documents/${account.id}/${Date.now()}-${safeFileName(file.name)}`;
      const fileRef = ref(storage, storagePath);
      await uploadBytes(fileRef, file, {
        contentType: file.type,
        customMetadata: { employeeId: String(identity.employeeId) },
      });
      const fileUrl = await getDownloadURL(fileRef);
      await addDoc(collection(db, "hr_documents"), {
        ...identity,
        documentType: documentForm.documentType,
        documentNumber: clean(documentForm.documentNumber),
        expiryDate: documentForm.expiryDate || "",
        fileName: file.name,
        fileUrl,
        storagePath,
        mimeType: file.type,
        fileSize: file.size,
        status: "PENDING",
        source: "EMPLOYEE_ESS",
        uploadedByUid: account.id,
        uploadedByRole: "EMPLOYEE",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setDocumentForm({
        documentType: "AADHAAR",
        documentNumber: "",
        expiryDate: "",
        file: null,
      });
      setDialog("");
      await refresh();
      notify("Document HR verification ke liye upload ho gaya.");
    } catch (error) {
      console.error(error);
      notify(`Document upload failed: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const submitRequest = async (event) => {
    event.preventDefault();
    if (!ensureEmployeeLink()) return;
    if (clean(requestForm.subject).length < 5) return notify("Request subject minimum 5 characters likhein.");
    if (clean(requestForm.description).length < 10) return notify("Request details minimum 10 characters likhein.");
    if (requestForm.requestType === "ATTENDANCE_CORRECTION" && !requestForm.eventDate) {
      return notify("Attendance correction date required hai.");
    }
    if (requestForm.requestType === "PROFILE_CHANGE" && (!requestForm.field || !requestForm.requestedValue)) {
      return notify("Profile field aur requested value required hai.");
    }
    setBusy(true);
    try {
      let attachmentUrl = "";
      let attachmentStoragePath = "";
      if (requestForm.attachment) {
        const file = requestForm.attachment;
        if (file.size > 5 * 1024 * 1024) throw new Error("Attachment maximum 5 MB hona chahiye.");
        if (!["application/pdf", "image/jpeg", "image/png"].includes(file.type)) {
          throw new Error("Attachment only PDF, JPG ya PNG ho sakta hai.");
        }
        attachmentStoragePath = `hr-employee-requests/${account.id}/${Date.now()}-${safeFileName(file.name)}`;
        const fileRef = ref(storage, attachmentStoragePath);
        await uploadBytes(fileRef, file, { contentType: file.type });
        attachmentUrl = await getDownloadURL(fileRef);
      }
      const requestNumber = `ESS-${new Date().getFullYear()}-${String(Date.now()).slice(-8)}`;
      await addDoc(collection(db, "hr_employee_requests"), {
        ...identity,
        requestNumber,
        requestType: requestForm.requestType,
        subject: clean(requestForm.subject),
        description: clean(requestForm.description),
        priority: requestForm.priority,
        eventDate: requestForm.eventDate || "",
        payload: {
          attendanceId: requestForm.attendanceId || "",
          date: requestForm.eventDate || "",
          requestedCheckIn: requestForm.requestedCheckIn || "",
          requestedCheckOut: requestForm.requestedCheckOut || "",
          field: requestForm.field || "",
          currentValue: clean(requestForm.currentValue),
          requestedValue: clean(requestForm.requestedValue),
        },
        attachmentUrl,
        attachmentStoragePath,
        status: "PENDING_HR_REVIEW",
        source: "EMPLOYEE_ESS",
        createdByUid: account.id,
        createdByRole: "EMPLOYEE",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setRequestForm({
        requestType: "HR_SUPPORT",
        subject: "",
        description: "",
        priority: "NORMAL",
        eventDate: "",
        requestedCheckIn: "",
        requestedCheckOut: "",
        field: "",
        currentValue: "",
        requestedValue: "",
        attendanceId: "",
        attachment: null,
      });
      setDialog("");
      await refresh();
      notify(`${requestNumber} HR Request Inbox me submit ho gaya.`);
    } catch (error) {
      console.error(error);
      notify(`HR request failed: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  const openAttendanceCorrection = (row = null) => {
    setRequestForm({
      requestType: "ATTENDANCE_CORRECTION",
      subject: `Attendance correction${row?.date ? ` - ${row.date}` : ""}`,
      description: "",
      priority: "NORMAL",
      eventDate: row?.date || "",
      requestedCheckIn: row?.checkInTime || "",
      requestedCheckOut: row?.checkOutTime || "",
      field: "",
      currentValue: "",
      requestedValue: "",
      attendanceId: row?.id || "",
      attachment: null,
    });
    setDialog("REQUEST");
  };

  const openRequest = (requestType = "HR_SUPPORT") => {
    const subjects = {
      PROFILE_CHANGE: "Employee profile change request",
      BANK_DETAILS_UPDATE: "Bank details update request",
      PAYSLIP_QUERY: "Salary / payslip query",
      LETTER_REQUEST: "Employment letter request",
      ASSET_SUPPORT: "Company asset support",
      GRIEVANCE: "Confidential grievance",
      HR_SUPPORT: "HR support request",
    };
    setRequestForm((current) => ({
      ...current,
      requestType,
      subject: subjects[requestType] || "",
      description: "",
      eventDate: "",
      attachment: null,
    }));
    setDialog("REQUEST");
  };

  const nav = [
    ["PORTAL", "Portal Home", Building2],
    ["OVERVIEW", "Employee Dashboard", UserCheck],
    ["ATTENDANCE", "Attendance", CalendarCheck],
    ["LEAVE", "Leave", Clock],
    ["REIMBURSEMENTS", "Reimbursements", FileCheck2],
    ["PAYROLL", "Payroll", Wallet],
    ["DOCUMENTS", "Documents", FileText],
    ["REQUESTS", "HR Requests", Mail],
  ];

  return (
    <div className="min-h-screen bg-[#f6f7f4] text-slate-900">
      {notice && <PortalNotice text={notice} />}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#10233f] text-white shadow-sm">
              <Building2 size={19} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-[#10233f] sm:text-base">Shine Agri People</p>
                <span className="hidden items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-emerald-700 sm:inline-flex"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500"/>Employee active</span>
              </div>
              <p className="max-w-[180px] truncate text-[11px] text-slate-500 sm:max-w-none">Employee self service · {employeeCode}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setActive("REQUESTS")} className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:bg-slate-50" aria-label="Open requests">
              <Bell size={17}/>
              {pendingCount > 0 && <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full border-2 border-white bg-amber-400 px-1 py-0.5 text-center text-[8px] font-bold text-slate-900">{pendingCount}</span>}
            </button>
            <Button tone="light" className="hidden sm:inline-flex" onClick={refresh}><RefreshCw size={15}/> Refresh</Button>
            <div className="hidden rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 lg:block">
              <p className="text-xs font-semibold text-slate-900">{employeeName}</p>
              <p className="text-[10px] text-slate-500">{identity.designation || "Employee"}</p>
            </div>
            <Button tone="dark" onClick={logout}><LogOut size={15}/><span className="hidden sm:inline">Logout</span></Button>
          </div>
        </div>
        <div className="border-t border-slate-100">
          <div className="mx-auto flex max-w-[1440px] gap-1 overflow-x-auto px-4 sm:px-6">
            {nav.map(([key, label, Icon]) => (
              <button
                key={key}
                type="button"
                onClick={() => setActive(key)}
                className={`relative flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-xs font-semibold transition ${
                  active === key
                    ? "border-blue-600 text-blue-700"
                    : "border-transparent text-slate-500 hover:border-slate-200 hover:text-slate-900"
                }`}
              >
                <Icon size={15}/>{label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        {active === "PORTAL" ? (
          <section className="relative overflow-hidden rounded-2xl border border-[#1d4f56] bg-[linear-gradient(115deg,#10233f_0%,#123c58_55%,#0f604b_100%)] px-5 py-6 text-white shadow-[0_16px_36px_rgba(16,35,63,.16)] sm:px-8 sm:py-8">
            <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full border border-white/10"/>
            <div className="absolute -bottom-24 right-28 h-52 w-52 rounded-full border border-emerald-200/10"/>
            <div className="relative grid items-center gap-6 lg:grid-cols-[1fr_auto]">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200/20 bg-white/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-100">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-300"/> Employee services portal
                </div>
                <h1 className="mt-4 text-2xl font-semibold tracking-tight sm:text-4xl">Welcome, {employeeName}</h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-200">
                  Attendance, leave, reimbursements, salary, documents and HR support—securely connected to your employee record.
                </p>
                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-300">
                  <span>{identity.designation || "Employee"}</span>
                  <span className="hidden text-white/30 sm:inline">•</span>
                  <span>{identity.department || "Department pending"}</span>
                  <span className="hidden text-white/30 sm:inline">•</span>
                  <span>Reports to {employee?.managerName || employee?.reportingManager || "HR"}</span>
                </div>
              </div>
              <div className="grid min-w-[260px] grid-cols-2 gap-px overflow-hidden rounded-xl border border-white/15 bg-white/10">
                <div className="bg-[#102f49]/90 p-4">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-300">Employee ID</p>
                  <p className="mt-1 text-sm font-semibold">{employeeCode}</p>
                </div>
                <div className="bg-[#102f49]/90 p-4">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-300">Profile</p>
                  <p className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-200"><CheckCircle2 size={14}/> Verified</p>
                </div>
                <div className="col-span-2 bg-[#102f49]/90 px-4 py-3">
                  <div className="flex items-center justify-between gap-3"><span className="text-[10px] text-slate-300">Open actions</span><span className="text-sm font-semibold">{pendingCount}</span></div>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <section className="rounded-2xl border border-slate-200 bg-white px-5 py-5 shadow-[0_1px_2px_rgba(15,23,42,.04)] sm:px-7">
            <div className="flex flex-wrap items-center justify-between gap-5">
              <div>
                <button type="button" onClick={() => setActive("PORTAL")} className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 transition hover:text-blue-800"><ArrowLeft size={14}/> Portal Home</button>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-600">Employee workspace</p>
                <h1 className="mt-1.5 text-2xl font-semibold tracking-tight text-[#10233f] sm:text-3xl">{nav.find(([key]) => key === active)?.[1] || "Employee services"}</h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-500">
                  {identity.designation || "Employee"} · {identity.department || "Department pending"} · Reports to {employee?.managerName || employee?.reportingManager || "HR"}
                </p>
              </div>
              <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700"><ShieldCheck size={18}/></div>
                <div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Profile status</p><p className="text-sm font-semibold text-slate-900">Active & verified</p></div>
              </div>
            </div>
          </section>
        )}

        {active === "PORTAL" && (
          <section>
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-700">Self service directory</p>
                <h2 className="mt-1 text-2xl font-semibold tracking-tight text-[#10233f]">Choose a service</h2>
                <p className="mt-1 text-sm text-slate-500">Live information from your employee profile and HR workflows.</p>
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500"/> All systems connected</div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              <PortalGatewayTile
                icon={UserCheck}
                title="Employee Dashboard"
                eyebrow="My workspace"
                value={`${pendingCount} open item${pendingCount === 1 ? "" : "s"}`}
                detail={`${attendanceRate}% attendance across recorded days`}
                tone="blue"
                onClick={() => setActive("OVERVIEW")}
              />
              <PortalGatewayTile
                icon={CalendarCheck}
                title="Attendance"
                eyebrow="Time & presence"
                value={todayAttendance?.checkInTime || "Not marked"}
                detail={`${presentDays} present of ${monthAttendance.length} monthly records`}
                tone="cyan"
                onClick={() => setActive("ATTENDANCE")}
              />
              <PortalGatewayTile
                icon={Clock}
                title="Leave & Comp-Off"
                eyebrow="Time away"
                value={`${totalLeaveBalance} days available`}
                detail={`${openLeaveCount} request${openLeaveCount === 1 ? "" : "s"} pending`}
                tone="green"
                onClick={() => setActive("LEAVE")}
              />
              <PortalGatewayTile
                icon={Wallet}
                title="Reimbursements"
                eyebrow="Expense claims"
                value={`${openReimbursementCount} pending`}
                detail={`${reimbursements.length} total claim${reimbursements.length === 1 ? "" : "s"}`}
                tone="violet"
                onClick={() => setActive("REIMBURSEMENTS")}
              />
              <PortalGatewayTile
                icon={Wallet}
                title="Salary & Payslips"
                eyebrow="Compensation"
                value={sortedPayroll[0] ? formatMoney(sortedPayroll[0].net) : "No release yet"}
                detail={sortedPayroll[0]?.month ? `Latest payroll: ${sortedPayroll[0].month}` : "Payslips appear after payroll release"}
                tone="slate"
                onClick={() => setActive("PAYROLL")}
              />
              <PortalGatewayTile
                icon={FileText}
                title="Document Vault"
                eyebrow="My records"
                value={`${verifiedDocumentCount}/${documents.length} verified`}
                detail={`${openDocumentCount} awaiting HR verification`}
                tone="amber"
                onClick={() => setActive("DOCUMENTS")}
              />
              <PortalGatewayTile
                icon={Mail}
                title="HR Help Desk"
                eyebrow="Employee support"
                value={`${openRequestCount} open request${openRequestCount === 1 ? "" : "s"}`}
                detail={`${employeeRequests.length} total service requests`}
                tone="slate"
                onClick={() => setActive("REQUESTS")}
              />
            </div>
          </section>
        )}

        {active === "OVERVIEW" && (
          <div className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat icon={CalendarCheck} label="Today's check-in" value={todayAttendance?.checkInTime || "Not marked"} helper={todayAttendance ? "Attendance captured" : "Use live photo + GPS attendance"} tone="blue" />
              <Stat icon={TrendingUp} label="Recorded-day attendance" value={`${attendanceRate}%`} helper={`${presentDays} present of ${monthAttendance.length} records`} tone="green" />
              <Stat icon={Clock} label="Open items" value={pendingCount} helper="Leave, claims, documents & HR requests" tone="amber" />
              <Stat icon={Wallet} label="Latest net pay" value={sortedPayroll[0] ? formatMoney(sortedPayroll[0].net) : "-"} helper={sortedPayroll[0]?.month || "No payroll released"} tone="slate" />
            </div>

            <div className="grid gap-5 lg:grid-cols-[1.45fr_.55fr]">
              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,.04)] sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div><p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-600">Self service</p><h2 className="mt-1 text-xl font-semibold text-[#10233f]">What would you like to do?</h2></div>
                  <p className="text-xs text-slate-500">Every submission creates a trackable HR workflow.</p>
                </div>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <QuickAction icon={Clock} title="Apply leave" text="CL, EL, OL, Comp-Off or LOP" onClick={() => setDialog("LEAVE")}/>
                  <QuickAction icon={Camera} title="Mark attendance" text="Live photo and GPS check-in or check-out" onClick={openAttendanceDialog}/>
                  <QuickAction icon={Wallet} title="Claim reimbursement" text="Submit expense details with receipt" onClick={() => setDialog("REIMBURSEMENT")}/>
                  <QuickAction icon={CalendarCheck} title="Correct attendance" text="Regularise a missing or incorrect punch" onClick={() => openAttendanceCorrection()}/>
                  <QuickAction icon={Upload} title="Upload document" text="Submit PDF, JPG or PNG for verification" onClick={() => setDialog("DOCUMENT")}/>
                  <QuickAction icon={Mail} title="Contact HR" text="Letters, payroll, assets or grievance" onClick={() => openRequest("HR_SUPPORT")}/>
                </div>
              </section>

              <section className="rounded-2xl bg-[#10233f] p-5 text-white shadow-[0_12px_28px_rgba(16,35,63,.12)] sm:p-6">
                <div className="flex items-center justify-between"><p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-cyan-300">Today</p><Activity size={18} className="text-cyan-300"/></div>
                <p className="mt-5 text-3xl font-semibold">{todayAttendance?.checkInTime || "--:--"}</p>
                <p className="mt-1 text-xs text-slate-300">Check-in time</p>
                <div className="my-5 h-px bg-white/10"/>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xl font-semibold">{pendingCount}</p><p className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">Open items</p></div>
                  <div><p className="text-xl font-semibold">{attendanceRate}%</p><p className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">Recorded days</p></div>
                </div>
                <button type="button" onClick={() => setActive("ATTENDANCE")} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 py-2.5 text-xs font-semibold transition hover:bg-white/15">View attendance <ChevronRight size={14}/></button>
              </section>
            </div>

            <div className="grid gap-5 lg:grid-cols-[1.2fr_.8fr]">
              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,.04)] sm:p-6">
                <div className="flex items-center justify-between gap-3"><div><p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Available leave</p><h2 className="mt-1 text-lg font-semibold text-[#10233f]">Balance overview</h2></div><button type="button" onClick={() => setActive("LEAVE")} className="text-xs font-semibold text-blue-700">View history</button></div>
                <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {Object.entries(balances).map(([type, value]) => (
                    <div key={type} className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                      <p className="text-2xl font-semibold text-[#10233f]">{value}</p>
                      <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{type.replace("_", " ")}</p>
                    </div>
                  ))}
                </div>
              </section>
              <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,.04)] sm:p-6">
                <div className="flex items-center justify-between"><div><p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Directory</p><h2 className="mt-1 text-lg font-semibold text-[#10233f]">Employee profile</h2></div><UserCheck size={20} className="text-blue-600"/></div>
                <div className="mt-4 grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  <ProfileRow label="Employee ID" value={employeeCode}/>
                  <ProfileRow label="Joining Date" value={employee?.joiningDate || "-"}/>
                  <ProfileRow label="Department" value={identity.department || "-"}/>
                  <ProfileRow label="Designation" value={identity.designation || "-"}/>
                </div>
                <Button tone="light" className="mt-4 w-full" onClick={() => openRequest("PROFILE_CHANGE")}>Request profile change</Button>
              </section>
            </div>
            {onboarding?.status !== "COMPLETED" && <OnboardingCard onboarding={onboarding}/>}
          </div>
        )}

        {active === "ATTENDANCE" && (
          <EssSection
            title="Daily Attendance"
            subtitle="Live camera photo, precise GPS and your verified employee identity are captured with every punch."
            action={
              <div className="flex flex-wrap gap-2">
                <Button onClick={openAttendanceDialog} disabled={!attendanceEnabled}>
                  <Camera size={16}/> Mark Attendance
                </Button>
                <Button tone="light" onClick={() => openAttendanceCorrection()}>
                  <CalendarCheck size={16}/> Request Correction
                </Button>
              </div>
            }
          >
            {!attendanceEnabled && (
              <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm font-medium text-amber-900">
                Attendance marking is locked until HR activates attendance on your employee record.
              </div>
            )}
            <div className="mb-5 grid gap-4 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Today</p>
                <p className="mt-1 text-lg font-semibold text-[#10233f]">{todayIso()}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Check-in</p>
                <p className="mt-1 text-lg font-semibold text-[#10233f]">{todayAttendance?.checkInTime || "--:--"}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Check-out</p>
                <p className="mt-1 text-lg font-semibold text-[#10233f]">{todayAttendance?.checkOutTime || "--:--"}</p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Daily status</p>
                <div className="mt-2"><Badge value={todayAttendance?.status || "NOT_MARKED"}/></div>
              </div>
            </div>
            <EssTable
              headers={["Date", "Check-in", "Check-out", "Attendance", "HR Review", "Action"]}
              rows={sortedAttendance.map((row) => [
                row.date || "-",
                row.checkInTime || "-",
                row.checkOutTime || "-",
                <Badge value={row.attendanceStatus || row.status || "PENDING"}/>,
                <Badge value={row.hrReviewStatus || "PENDING_HR_REVIEW"}/>,
                <button type="button" onClick={() => openAttendanceCorrection(row)} className="font-black text-blue-700">Correct</button>,
              ])}
            />
          </EssSection>
        )}

        {active === "LEAVE" && (
          <div className="grid gap-5 lg:grid-cols-[.7fr_1.3fr]">
            <EssSection title="Balances" subtitle="Approved leave ke baad HR balance update karega.">
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(balances).map(([type, value]) => (
                  <div key={type} className="rounded-xl border border-slate-200 bg-slate-50/70 p-4"><p className="text-3xl font-semibold text-[#10233f]">{value}</p><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{type.replace("_", " ")}</p></div>
                ))}
              </div>
              <Button className="mt-4 w-full" onClick={() => setDialog("LEAVE")}><Clock size={16}/> Apply New Leave</Button>
            </EssSection>
            <EssSection title="Leave history" subtitle="Manager/HR/CEO workflow status live track karein.">
              <EssTable
                headers={["Type", "Dates", "Days", "Reason", "Status"]}
                rows={[...leaves].sort((a,b) => String(b.fromDate).localeCompare(String(a.fromDate))).map((row) => [
                  row.type || "-",
                  `${row.fromDate || "-"} → ${row.toDate || "-"}`,
                  row.days || 0,
                  row.reason || "-",
                  <Badge value={row.status}/>,
                ])}
              />
            </EssSection>
          </div>
        )}

        {active === "REIMBURSEMENTS" && (
          <EssSection
            title="Expense Reimbursements"
            subtitle="Submit official expenses with a receipt and track HR review from one place."
            action={
              <Button onClick={() => setDialog("REIMBURSEMENT")}>
                <Wallet size={16}/> New Claim
              </Button>
            }
          >
            <div className="mb-5 grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                <p className="text-2xl font-semibold text-[#10233f]">{reimbursements.length}</p>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Total claims</p>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-2xl font-semibold text-amber-800">{openReimbursementCount}</p>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">Pending review</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-2xl font-semibold text-emerald-800">
                  {formatMoney(
                    reimbursements
                      .filter((item) => ["APPROVED", "PAID"].includes(item.status))
                      .reduce((total, item) => total + Number(item.amount || 0), 0)
                  )}
                </p>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Approved / paid</p>
              </div>
            </div>
            <EssTable
              headers={["Claim", "Category", "Expense Date", "Amount", "Purpose / Site", "Receipt", "Status"]}
              rows={sortedReimbursements.map((row) => [
                row.claimNumber || row.id,
                String(row.category || "OTHER").replaceAll("_", " "),
                row.expenseDate || "-",
                formatMoney(row.amount),
                <div>
                  <p className="font-semibold text-slate-900">{row.purpose || "-"}</p>
                  <p className="mt-1 text-xs text-slate-500">{row.projectSite || "-"}</p>
                </div>,
                row.receiptFileName ? (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                    <FileCheck2 size={14}/> Stored securely
                  </span>
                ) : (
                  "-"
                ),
                <Badge value={row.status}/>,
              ])}
            />
          </EssSection>
        )}

        {active === "PAYROLL" && (
          <EssSection title="Salary & Payslips" subtitle="Payroll read-only hai. Query ho to HR request raise karein." action={<Button tone="light" onClick={() => openRequest("PAYSLIP_QUERY")}><Mail size={16}/> Raise Payroll Query</Button>}>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {sortedPayroll.map((row) => (
                <article key={row.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,.04)]">
                  <div className="flex items-start justify-between gap-3"><div><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Salary month</p><h3 className="mt-1 text-xl font-semibold text-[#10233f]">{row.month || "-"}</h3></div><Badge value={row.paymentStatus || row.status}/></div>
                  <div className="mt-5 grid grid-cols-2 gap-2 text-sm"><ProfileRow label="Gross" value={formatMoney(row.gross)}/><ProfileRow label="Deductions" value={formatMoney(row.totalDeduction)}/><ProfileRow label="Net Pay" value={formatMoney(row.net)}/><ProfileRow label="UTR" value={row.utrNumber || "-"}/></div>
                  {row.payslipUrl ? <a href={row.payslipUrl} target="_blank" rel="noreferrer" className="mt-4 inline-flex w-full justify-center rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700">View / Download Payslip</a> : <p className="mt-4 rounded-xl bg-slate-50 p-3 text-center text-xs font-medium text-slate-500">Payslip PDF payroll release ke baad available hogi.</p>}
                </article>
              ))}
              {!sortedPayroll.length && <EmptyMini text="No payroll record available"/>}
            </div>
          </EssSection>
        )}

        {active === "DOCUMENTS" && (
          <EssSection title="My Document Vault" subtitle="Document upload ke baad HR verify/query/reject status yahan dikhai dega." action={<Button onClick={() => setDialog("DOCUMENT")}><Upload size={16}/> Upload Document</Button>}>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {documents.map((row) => (
                <article key={row.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,.04)]">
                  <div className="flex items-start justify-between gap-3"><FileText className="text-blue-700"/><Badge value={row.status}/></div>
                  <h3 className="mt-4 font-semibold text-[#10233f]">{row.documentType || "Document"}</h3>
                  <p className="mt-1 text-xs text-slate-500">{row.documentNumber || "Number not provided"}</p>
                  {row.fileUrl && <a href={row.fileUrl} target="_blank" rel="noreferrer" className="mt-4 inline-flex text-sm font-semibold text-blue-700">Open document →</a>}
                </article>
              ))}
              {!documents.length && <EmptyMini text="No document uploaded"/>}
            </div>
          </EssSection>
        )}

        {active === "REQUESTS" && (
          <div className="space-y-5">
            <EssSection title="HR Service Catalogue" subtitle="Attendance, profile, bank, payroll, letter, asset ya grievance request raise karein.">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  ["ATTENDANCE_CORRECTION", "Attendance correction", CalendarCheck],
                  ["PROFILE_CHANGE", "Profile update", UserCheck],
                  ["BANK_DETAILS_UPDATE", "Bank details", Wallet],
                  ["PAYSLIP_QUERY", "Payroll query", Wallet],
                  ["LETTER_REQUEST", "HR letter", FileText],
                  ["ASSET_SUPPORT", "Asset support", BriefcaseBusiness],
                  ["GRIEVANCE", "Grievance", ShieldCheck],
                  ["HR_SUPPORT", "Other HR help", Mail],
                ].map(([type, label, Icon]) => (
                  <QuickAction key={type} icon={Icon} title={label} text="Create trackable request" onClick={() => type === "ATTENDANCE_CORRECTION" ? openAttendanceCorrection() : openRequest(type)}/>
                ))}
              </div>
            </EssSection>
            <EssSection title="My requests" subtitle="HR action aur resolution live track karein.">
              <EssTable
                headers={["Request No.", "Type", "Subject", "Priority", "Created", "Status"]}
                rows={[...employeeRequests].sort((a,b) => String(b.requestNumber).localeCompare(String(a.requestNumber))).map((row) => [
                  row.requestNumber || row.id,
                  String(row.requestType || "").replaceAll("_", " "),
                  row.subject || "-",
                  row.priority || "NORMAL",
                  formatDate(row.createdAt),
                  <Badge value={row.status}/>,
                ])}
              />
            </EssSection>
          </div>
        )}
      </main>

      {dialog === "ATTENDANCE_MARK" && (
        <Modal
          title="Secure Daily Attendance"
          onClose={() => {
            if (!busy) closeAttendanceDialog();
          }}
          wide
        >
          <div className="grid gap-5 lg:grid-cols-[1.35fr_.65fr]">
            <section>
              <div className="relative aspect-video overflow-hidden rounded-2xl bg-slate-950">
                {capturedPhotoPreview ? (
                  <img
                    src={capturedPhotoPreview}
                    alt="Captured attendance evidence"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <>
                    <video
                      ref={videoRef}
                      muted
                      playsInline
                      className="h-full w-full object-cover"
                    />
                    {!cameraStarted && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-white">
                        <Camera size={42} className="text-cyan-300"/>
                        <p className="mt-3 text-sm font-semibold">Start camera for a live attendance photo</p>
                        <p className="mt-1 text-xs text-slate-400">Your authenticated employee identity is used automatically.</p>
                      </div>
                    )}
                  </>
                )}
                <canvas ref={canvasRef} className="hidden"/>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {!cameraStarted && !capturedPhoto && (
                  <Button tone="dark" onClick={startAttendanceCamera} disabled={busy}>
                    <Camera size={16}/> Start Camera
                  </Button>
                )}
                {cameraStarted && (
                  <Button onClick={captureAttendancePhoto} disabled={busy}>
                    <Camera size={16}/> Capture Photo
                  </Button>
                )}
                {capturedPhoto && (
                  <Button
                    tone="light"
                    onClick={() => {
                      setCapturedPhoto(null);
                      setCapturedPhotoPreview("");
                      startAttendanceCamera();
                    }}
                    disabled={busy}
                  >
                    <RefreshCw size={16}/> Retake
                  </Button>
                )}
                <Button
                  tone="light"
                  onClick={verifyAttendanceLocation}
                  disabled={busy || attendanceAction === "LOCATION"}
                >
                  <MapPin size={16}/>
                  {attendanceAction === "LOCATION" ? "Capturing GPS..." : "Verify GPS"}
                </Button>
              </div>
            </section>

            <aside className="space-y-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Verified employee</p>
                <p className="mt-2 font-semibold text-[#10233f]">{employeeName}</p>
                <p className="mt-1 text-xs text-slate-500">{employeeCode} · {identity.designation || "Employee"}</p>
              </div>
              <div className={`rounded-2xl border p-4 ${locationInfo ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
                <div className="flex items-center gap-2">
                  <MapPin size={17} className={locationInfo ? "text-emerald-700" : "text-amber-700"}/>
                  <p className="text-xs font-semibold uppercase tracking-wider">GPS evidence</p>
                </div>
                {locationInfo ? (
                  <div className="mt-3 space-y-1 text-xs text-slate-700">
                    <p>Accuracy: ±{Math.round(locationInfo.accuracy)} metres</p>
                    <p>
                      Office match: {locationInfo.locationVerified ? "Verified" : "HR review required"}
                    </p>
                    {locationInfo.officeDistanceMeters !== null && (
                      <p>Office distance: {locationInfo.officeDistanceMeters} metres</p>
                    )}
                  </div>
                ) : (
                  <p className="mt-2 text-xs leading-5 text-amber-800">
                    GPS will be requested before submission if it is not verified now.
                  </p>
                )}
              </div>
              <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-xs leading-5 text-blue-900">
                <p className="font-semibold">Today: {todayIso()}</p>
                <p className="mt-1">Check-in: {todayAttendance?.checkInTime || "Not marked"}</p>
                <p>Check-out: {todayAttendance?.checkOutTime || "Not marked"}</p>
              </div>
            </aside>
          </div>

          <div className="mt-5 grid gap-3 border-t border-slate-200 pt-5 sm:grid-cols-2">
            <Button
              tone="green"
              onClick={() => markAttendance("CHECK_IN")}
              disabled={
                busy ||
                attendanceAction === "LOCATION" ||
                !capturedPhoto ||
                Boolean(todayAttendance?.checkInTime || todayAttendance?.checkInAt)
              }
            >
              <LogIn size={17}/>
              {busy && attendanceAction === "CHECK_IN" ? "Saving Check-in..." : "Check In"}
            </Button>
            <Button
              tone="dark"
              onClick={() => markAttendance("CHECK_OUT")}
              disabled={
                busy ||
                attendanceAction === "LOCATION" ||
                !capturedPhoto ||
                !Boolean(todayAttendance?.checkInTime || todayAttendance?.checkInAt) ||
                Boolean(todayAttendance?.checkOutTime || todayAttendance?.checkOutAt)
              }
            >
              <LogOut size={17}/>
              {busy && attendanceAction === "CHECK_OUT" ? "Saving Check-out..." : "Check Out"}
            </Button>
          </div>
          <p className="mt-3 text-center text-[11px] text-slate-500">
            Photo files remain in protected Firebase Storage; the attendance record stores only the storage path and evidence metadata.
          </p>
        </Modal>
      )}

      {dialog === "REIMBURSEMENT" && (
        <Modal title="Submit Reimbursement Claim" onClose={() => setDialog("")} wide>
          <form onSubmit={submitReimbursement} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <Select
                label="Category"
                value={reimbursementForm.category}
                onChange={(event) =>
                  setReimbursementForm({
                    ...reimbursementForm,
                    category: event.target.value,
                  })
                }
              >
                <option value="TRAVEL">Travel</option>
                <option value="LOCAL_CONVEYANCE">Local Conveyance</option>
                <option value="LODGING">Lodging</option>
                <option value="MEALS">Meals</option>
                <option value="OTHER">Other</option>
              </Select>
              <Field
                label="Amount (₹)"
                type="number"
                min="0.01"
                max="10000000"
                step="0.01"
                value={reimbursementForm.amount}
                onChange={(event) =>
                  setReimbursementForm({
                    ...reimbursementForm,
                    amount: event.target.value,
                  })
                }
              />
              <Field
                label="Expense Date"
                type="date"
                max={todayIso()}
                value={reimbursementForm.expenseDate}
                onChange={(event) =>
                  setReimbursementForm({
                    ...reimbursementForm,
                    expenseDate: event.target.value,
                  })
                }
              />
            </div>
            <Field
              label="Project / Site"
              placeholder="Project, branch or site name"
              value={reimbursementForm.projectSite}
              onChange={(event) =>
                setReimbursementForm({
                  ...reimbursementForm,
                  projectSite: event.target.value,
                })
              }
            />
            {["TRAVEL", "LOCAL_CONVEYANCE"].includes(reimbursementForm.category) && (
              <div className="grid gap-4 sm:grid-cols-3">
                <Field
                  label="Transport Mode"
                  placeholder="Train, cab, bus..."
                  value={reimbursementForm.transportMode}
                  onChange={(event) =>
                    setReimbursementForm({
                      ...reimbursementForm,
                      transportMode: event.target.value,
                    })
                  }
                />
                <Field
                  label="From"
                  value={reimbursementForm.fromLocation}
                  onChange={(event) =>
                    setReimbursementForm({
                      ...reimbursementForm,
                      fromLocation: event.target.value,
                    })
                  }
                />
                <Field
                  label="To"
                  value={reimbursementForm.toLocation}
                  onChange={(event) =>
                    setReimbursementForm({
                      ...reimbursementForm,
                      toLocation: event.target.value,
                    })
                  }
                />
              </div>
            )}
            <label className="block">
              <span className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Business Purpose</span>
              <textarea
                rows={4}
                placeholder="Explain the official purpose and relevant details"
                value={reimbursementForm.purpose}
                onChange={(event) =>
                  setReimbursementForm({
                    ...reimbursementForm,
                    purpose: event.target.value,
                  })
                }
                className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm font-medium text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              />
            </label>
            <label className="block rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50 p-5 text-center">
              <Upload className="mx-auto text-blue-700"/>
              <span className="mt-2 block text-xs font-semibold text-slate-700">Upload receipt · PDF/JPG/PNG · Max 10 MB</span>
              <input
                className="mt-3 text-sm"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(event) =>
                  setReimbursementForm({
                    ...reimbursementForm,
                    receipt: event.target.files?.[0] || null,
                  })
                }
              />
            </label>
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900">
              Submitted claims are locked for HR review. Verify amount, date and receipt before submitting.
            </div>
            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? "Submitting Claim..." : "Submit for HR Review"}
            </Button>
          </form>
        </Modal>
      )}

      {dialog === "LEAVE" && (
        <Modal title="Apply Leave" onClose={() => setDialog("")}>
          <form onSubmit={submitLeave} className="space-y-4">
            <Select label="Leave Type" value={leaveForm.type} onChange={(e) => setLeaveForm({...leaveForm, type:e.target.value})}>
              <option value="CL">Casual Leave (CL)</option><option value="EL">Earned Leave (EL)</option><option value="OL">Optional Leave (OL)</option><option value="COMP_OFF">Comp-Off</option><option value="LOP">Loss of Pay (LOP)</option>
            </Select>
            <div className="grid gap-4 sm:grid-cols-2"><Field label="From Date" type="date" min={todayIso()} value={leaveForm.fromDate} onChange={(e)=>setLeaveForm({...leaveForm,fromDate:e.target.value})}/><Field label="To Date" type="date" min={leaveForm.fromDate || todayIso()} value={leaveForm.toDate} onChange={(e)=>setLeaveForm({...leaveForm,toDate:e.target.value})}/></div>
            <label className="block"><span className="mb-1.5 block text-[11px] font-black uppercase tracking-wider text-slate-500">Reason</span><textarea rows={4} value={leaveForm.reason} onChange={(e)=>setLeaveForm({...leaveForm,reason:e.target.value})} className="w-full rounded-xl border border-slate-200 p-3 text-sm font-semibold outline-none focus:border-blue-500"/></label>
            <div className="rounded-xl bg-blue-50 p-3 text-sm font-bold text-blue-800">Requested days: {leaveDays(leaveForm.fromDate, leaveForm.toDate)}</div>
            <Button type="submit" className="w-full" disabled={busy}>{busy ? "Submitting..." : "Submit to HR"}</Button>
          </form>
        </Modal>
      )}

      {dialog === "DOCUMENT" && (
        <Modal title="Upload Employee Document" onClose={() => setDialog("")}>
          <form onSubmit={submitDocument} className="space-y-4">
            <Select label="Document Type" value={documentForm.documentType} onChange={(e)=>setDocumentForm({...documentForm,documentType:e.target.value})}>
              <option value="AADHAAR">Aadhaar</option><option value="PAN">PAN</option><option value="BANK_PROOF">Bank Proof</option><option value="QUALIFICATION">Qualification</option><option value="EXPERIENCE">Experience</option><option value="ADDRESS_PROOF">Address Proof</option><option value="OTHER">Other</option>
            </Select>
            <Field label="Document Number (optional)" value={documentForm.documentNumber} onChange={(e)=>setDocumentForm({...documentForm,documentNumber:e.target.value})}/>
            <Field label="Expiry Date (if applicable)" type="date" value={documentForm.expiryDate} onChange={(e)=>setDocumentForm({...documentForm,expiryDate:e.target.value})}/>
            <label className="block rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50 p-5 text-center"><Upload className="mx-auto text-blue-700"/><span className="mt-2 block text-xs font-bold text-slate-600">PDF/JPG/PNG • Max 5 MB</span><input className="mt-3 text-sm" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e)=>setDocumentForm({...documentForm,file:e.target.files?.[0]||null})}/></label>
            <Button type="submit" className="w-full" disabled={busy}>{busy ? "Uploading..." : "Upload for HR Verification"}</Button>
          </form>
        </Modal>
      )}

      {dialog === "REQUEST" && (
        <Modal title="Create HR Service Request" onClose={() => setDialog("")} wide>
          <form onSubmit={submitRequest} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Select label="Request Type" value={requestForm.requestType} onChange={(e)=>setRequestForm({...requestForm,requestType:e.target.value})}>
                <option value="ATTENDANCE_CORRECTION">Attendance Correction</option><option value="PROFILE_CHANGE">Profile Change</option><option value="BANK_DETAILS_UPDATE">Bank Details Update</option><option value="PAYSLIP_QUERY">Payroll / Payslip Query</option><option value="LETTER_REQUEST">Employment Letter</option><option value="ASSET_SUPPORT">Asset Support</option><option value="GRIEVANCE">Grievance</option><option value="HR_SUPPORT">General HR Support</option>
              </Select>
              <Select label="Priority" value={requestForm.priority} onChange={(e)=>setRequestForm({...requestForm,priority:e.target.value})}><option value="NORMAL">Normal</option><option value="HIGH">High</option><option value="URGENT">Urgent</option></Select>
            </div>
            <Field label="Subject" value={requestForm.subject} onChange={(e)=>setRequestForm({...requestForm,subject:e.target.value})}/>
            {requestForm.requestType === "ATTENDANCE_CORRECTION" && <div className="grid gap-4 sm:grid-cols-3"><Field label="Attendance Date" type="date" max={todayIso()} value={requestForm.eventDate} onChange={(e)=>setRequestForm({...requestForm,eventDate:e.target.value})}/><Field label="Requested Check-in" type="time" value={requestForm.requestedCheckIn} onChange={(e)=>setRequestForm({...requestForm,requestedCheckIn:e.target.value})}/><Field label="Requested Check-out" type="time" value={requestForm.requestedCheckOut} onChange={(e)=>setRequestForm({...requestForm,requestedCheckOut:e.target.value})}/></div>}
            {requestForm.requestType === "PROFILE_CHANGE" && <div className="grid gap-4 sm:grid-cols-3"><Select label="Field" value={requestForm.field} onChange={(e)=>setRequestForm({...requestForm,field:e.target.value})}><option value="">Select</option><option value="mobile">Mobile</option><option value="address">Address</option><option value="emergencyContactName">Emergency Contact Name</option><option value="emergencyContactMobile">Emergency Contact Mobile</option></Select><Field label="Current Value" value={requestForm.currentValue} onChange={(e)=>setRequestForm({...requestForm,currentValue:e.target.value})}/><Field label="Requested Value" value={requestForm.requestedValue} onChange={(e)=>setRequestForm({...requestForm,requestedValue:e.target.value})}/></div>}
            <label className="block"><span className="mb-1.5 block text-[11px] font-black uppercase tracking-wider text-slate-500">Complete Details</span><textarea rows={5} value={requestForm.description} onChange={(e)=>setRequestForm({...requestForm,description:e.target.value})} className="w-full rounded-xl border border-slate-200 p-3 text-sm font-semibold outline-none focus:border-blue-500"/></label>
            <label className="block"><span className="mb-1.5 block text-[11px] font-black uppercase tracking-wider text-slate-500">Attachment (optional)</span><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e)=>setRequestForm({...requestForm,attachment:e.target.files?.[0]||null})} className="w-full rounded-xl border border-slate-200 p-3 text-sm"/></label>
            <Button type="submit" className="w-full" disabled={busy}>{busy ? "Submitting..." : "Submit to HR Request Inbox"}</Button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function PortalGatewayTile({
  icon: Icon,
  title,
  eyebrow,
  value,
  detail,
  tone = "blue",
  onClick,
}) {
  const tones = {
    blue: {
      icon: "border-blue-100 bg-blue-50 text-blue-600",
      accent: "bg-blue-600",
      hover: "hover:border-blue-200 hover:shadow-[0_14px_32px_rgba(37,99,235,.10)]",
    },
    cyan: {
      icon: "border-cyan-100 bg-cyan-50 text-cyan-700",
      accent: "bg-cyan-600",
      hover: "hover:border-cyan-200 hover:shadow-[0_14px_32px_rgba(8,145,178,.10)]",
    },
    green: {
      icon: "border-emerald-100 bg-emerald-50 text-emerald-700",
      accent: "bg-emerald-600",
      hover: "hover:border-emerald-200 hover:shadow-[0_14px_32px_rgba(5,150,105,.10)]",
    },
    violet: {
      icon: "border-violet-100 bg-violet-50 text-violet-600",
      accent: "bg-violet-600",
      hover: "hover:border-violet-200 hover:shadow-[0_14px_32px_rgba(124,58,237,.10)]",
    },
    amber: {
      icon: "border-amber-100 bg-amber-50 text-amber-700",
      accent: "bg-amber-400",
      hover: "hover:border-amber-200 hover:shadow-[0_14px_32px_rgba(217,119,6,.10)]",
    },
    slate: {
      icon: "border-slate-200 bg-slate-100 text-slate-700",
      accent: "bg-[#10233f]",
      hover: "hover:border-slate-300 hover:shadow-[0_14px_32px_rgba(16,35,63,.10)]",
    },
  };
  const selectedTone = tones[tone] || tones.blue;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open ${title}`}
      className={`group relative min-h-[210px] overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-[0_1px_2px_rgba(15,23,42,.04)] transition duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-100 ${selectedTone.hover}`}
    >
      <span className={`absolute inset-x-0 top-0 h-1 ${selectedTone.accent}`}/>
      <div className="flex items-start justify-between gap-4">
        <div className={`flex h-12 w-12 items-center justify-center rounded-2xl border ${selectedTone.icon}`}><Icon size={22}/></div>
        <div className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:text-blue-600"><ChevronRight size={16}/></div>
      </div>
      <p className="mt-5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">{eyebrow}</p>
      <h3 className="mt-1 text-xl font-semibold tracking-tight text-[#10233f]">{title}</h3>
      <div className="mt-5 border-t border-slate-100 pt-4">
        <p className="text-sm font-semibold text-slate-900">{value}</p>
        <p className="mt-1 text-xs leading-5 text-slate-500">{detail}</p>
      </div>
    </button>
  );
}

function QuickAction({ icon: Icon, title, text, onClick }) {
  return (
    <button type="button" onClick={onClick} className="group flex items-start gap-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4 text-left transition duration-200 hover:border-blue-200 hover:bg-blue-50/60 hover:shadow-[0_8px_22px_rgba(37,99,235,.08)]">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-blue-600 transition group-hover:border-blue-200 group-hover:bg-blue-600 group-hover:text-white"><Icon size={18}/></div>
      <div>
        <p className="text-sm font-semibold text-[#10233f]">{title}</p>
        <p className="mt-1 text-xs leading-5 text-slate-500">{text}</p>
      </div>
      <ChevronRight size={16} className="ml-auto mt-3 shrink-0 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-blue-500"/>
    </button>
  );
}

function EssSection({ title, subtitle, action = null, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,.04)] sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h2 className="text-xl font-semibold tracking-tight text-[#10233f]">{title}</h2><p className="mt-1.5 text-xs leading-5 text-slate-500">{subtitle}</p></div>
        {action}
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function EssTable({ headers, rows }) {
  if (!rows.length) return <EmptyMini text="No record available"/>;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="min-w-full border-collapse text-left">
        <thead className="border-b border-slate-200 bg-slate-50"><tr>{headers.map((header)=><th key={header} className="whitespace-nowrap px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">{header}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-100">{rows.map((cells,index)=><tr key={index} className="bg-white transition hover:bg-slate-50/70">{cells.map((cell,cellIndex)=><td key={cellIndex} className="max-w-xs px-4 py-3.5 text-xs font-medium text-slate-700">{cell}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function ProfileRow({ label, value }) {
  return <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-3"><p className="text-[9px] font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</p><p className="mt-1 break-words text-sm font-semibold text-[#10233f]">{value || "-"}</p></div>;
}

function EmptyMini({ text }) {
  return <div className="col-span-full rounded-xl border border-dashed border-slate-300 bg-slate-50/60 p-8 text-center text-sm font-medium text-slate-500">{text}</div>;
}

function ApplicationTimeline({ item, acceptOffer, openInterview }) {
  const currentIndex = APPLICATION_STAGES.findIndex(([status]) => status === item.status);
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,.04)] sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div><p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-600">{item.applicationNumber}</p><h2 className="mt-2 text-xl font-semibold tracking-tight text-[#10233f] sm:text-2xl">{item.jobTitle}</h2><p className="mt-1.5 text-sm text-slate-500">{item.deptName || item.department} · Applied {formatDate(item.createdAt)}</p></div>
        <div className="text-right"><Badge value={item.status}/><p className="mt-2 text-xs font-medium text-slate-500">ATS match {item.atsScore || 0}/100</p></div>
      </div>
      <div className="mt-7 overflow-x-auto pb-1">
        <div className="grid min-w-[900px] grid-cols-8">
          {APPLICATION_STAGES.map(([status, label], index) => {
            const done = index <= currentIndex && currentIndex >= 0;
            return (
              <div key={status} className="relative pr-3">
                {index < APPLICATION_STAGES.length - 1 && <div className={`absolute left-7 right-0 top-3.5 h-px ${index < currentIndex ? "bg-emerald-400" : "bg-slate-200"}`}/>}
                <div className={`relative z-10 flex h-7 w-7 items-center justify-center rounded-full border text-[10px] font-semibold ${done ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-400"}`}>{done ? "✓" : index+1}</div>
                <p className={`mt-2 max-w-[105px] text-[10px] font-medium leading-4 ${done ? "text-slate-900" : "text-slate-400"}`}>{label}</p>
              </div>
            );
          })}
        </div>
      </div>
      {(item.interviewDateTime ||
        item.scheduledStartAt ||
        item.scheduledStart) && (
        <div className="mt-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-violet-200 bg-violet-50 p-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-violet-600">
              Secure Video Interview
            </p>
            <p className="mt-1 font-semibold text-violet-950">
              {formatDate(
                item.scheduledStartAt ||
                  item.scheduledStart ||
                  item.interviewDateTime
              )}{" "}
              •{" "}
              {item.interviewMode || item.mode || "ONLINE"}
            </p>
            <p className="mt-1 text-xs font-semibold text-violet-700">
              Link scheduled time par activate hoga aur 60 minutes baad expire
              ho jayega.
            </p>
          </div>
          {(item.interviewInviteId || item.inviteId) && (
            <Button
              tone="green"
              onClick={() =>
                openInterview(item.interviewInviteId || item.inviteId)
              }
            >
              <CalendarCheck size={17} /> Open Interview Lobby
            </Button>
          )}
        </div>
      )}
      {item.status === "OFFER_RELEASED" && <div className="mt-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4"><div><p className="text-xs font-black uppercase text-emerald-600">Employment Offer Available</p><p className="mt-1 text-sm font-bold text-emerald-900">Offer accept karne ke baad HR employee activation start karega.</p></div><Button tone="green" onClick={() => acceptOffer(item)}><CheckCircle2 size={17}/> Accept Offer</Button></div>}
      {item.status === "REJECTED" && <div className="mt-5 flex gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700"><XCircle size={20}/>Application closed. Candidate account access will be blocked as per recruitment policy.</div>}
    </section>
  );
}

function OnboardingCard({ onboarding }) {
  const steps = [["documentCollection","Document Verification"],["idCreated","Employee ID"],["roleMapped","Role Mapping"],["hierarchySetup","Hierarchy Setup"],["attendanceActivated","Attendance Activation"]];
  return <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_1px_2px_rgba(15,23,42,.04)]"><p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-blue-600">Employee lifecycle</p><h2 className="mt-1 text-xl font-semibold text-[#10233f]">Activation progress</h2><div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">{steps.map(([key,label])=><div key={key} className={`rounded-xl border p-4 ${onboarding?.[key]?"border-emerald-200 bg-emerald-50/70":"border-amber-200 bg-amber-50/70"}`}><div className={`flex h-9 w-9 items-center justify-center rounded-xl ${onboarding?.[key]?"bg-emerald-600 text-white":"bg-amber-400 text-slate-950"}`}>{onboarding?.[key]?<CheckCircle2 size={18}/>:<Clock size={18}/>}</div><p className="mt-3 text-xs font-semibold text-slate-900">{label}</p><p className="mt-1 text-[9px] font-semibold uppercase tracking-wider text-slate-500">{onboarding?.[key]?"Completed":"Pending HR"}</p></div>)}</div></section>;
}

function Stat({ icon: Icon, label, value, helper = "", tone = "blue" }) {
  const tones = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    slate: "bg-slate-100 text-slate-600",
  };
  return <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,.04)]"><div className="flex items-start justify-between gap-3"><div><p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500">{label}</p><p className="mt-2 text-2xl font-semibold tracking-tight text-[#10233f]">{value}</p></div><div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tones[tone] || tones.blue}`}><Icon size={18}/></div></div>{helper && <p className="mt-3 text-[11px] leading-4 text-slate-500">{helper}</p>}</div>;
}

function ListCard({ title, icon: Icon, rows }) {
  return <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,.04)]"><h3 className="flex items-center gap-2 text-lg font-semibold text-[#10233f]"><Icon size={18} className="text-blue-600"/>{title}</h3><div className="mt-4 space-y-2">{rows.map(([label,status,url],index)=><div key={`${label}-${index}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/70 p-3"><div><p className="text-sm font-medium text-slate-900">{label}</p>{url&&<a href={url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-blue-600">View file</a>}</div><Badge value={status}/></div>)}{!rows.length&&<p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">No record available</p>}</div></section>;
}

function Empty({ title, text, action = null }) {
  return <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center"><div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400"><FileCheck2 size={23}/></div><h2 className="mt-4 text-xl font-semibold text-[#10233f]">{title}</h2><p className="mt-2 text-sm text-slate-500">{text}</p>{action&&<div className="mt-5 flex justify-center">{action}</div>}</div>;
}
